import Amplify, { API as AWSAPI, graphqlOperation, Auth } from "aws-amplify";
import * as mutations from "../graphql/mutations";
import * as queries from "../graphql/queries";
import * as subscriptions from "../graphql/subscriptions";
import * as custom from "../graphql/custom";
import Constant from "../helpers/Constants";
import _ from "lodash";
import {
  IS_REPORT_V2_TEST,
  REPORT_API_VERSION,
  REPORT_V2_TEST_VERSION,
  isReportV2,
} from "../helpers/reportV2";

import { AUTH_TYPE } from "aws-appsync";
import { removeTypename } from "../helpers/Utils";
import { getExpDate } from "../reducers/local";
import { installDemoInterceptor } from "../demo/interceptor";

// 브랜드메시지 캠페인의 listItems/cards/tail 은 AWSJSON 이라 조회 시 문자열로 내려오고
// 저장 시에는 문자열로 올려야 한다. 나머지 캠페인 필드는 손대지 않는다.
// 파싱 책임은 API 계층이다 — 조회(getCampaign/list*)뿐 아니라 캠페인을 돌려주는
// mutation 응답(create/update/launchCampaign)도 태워야 redux 에 JSON 문자열이 남지
// 않는다(BM-48). 반대 방향(요청 직전 stringify)은 serializeBrandCampaign 이 맡는다.
// coupon 은 BM-29 네이티브 오브젝트(Campaign.coupon: AWSJSON) — 빠뜨리면 문자열/객체가
// 어긋나 저장·조회 어느 쪽에서든 쿠폰이 조용히 사라진다.
const BRAND_JSON_FIELDS = ["listItems", "cards", "tail", "coupon"];

const parseBrandCampaign = (campaign) => {
  if (!campaign) {
    return campaign;
  }
  const patch = {};
  let changed = false;
  BRAND_JSON_FIELDS.forEach((field) => {
    if (typeof campaign[field] === "string") {
      changed = true;
      try {
        patch[field] = JSON.parse(campaign[field]);
      } catch (e) {
        patch[field] = null;
      }
    }
  });
  return changed ? { ...campaign, ...patch } : campaign;
};

const parseBrandCampaignList = (result) =>
  result && Array.isArray(result.items)
    ? { ...result, items: result.items.map(parseBrandCampaign) }
    : result;

// createCampaign/updateCampaign 과 동일하게 인자를 직접 변형한다(기존 스타일).
const serializeBrandCampaign = (campaign) => {
  BRAND_JSON_FIELDS.forEach((field) => {
    const value = campaign[field];
    if (value !== undefined && value !== null && typeof value !== "string") {
      campaign[field] = JSON.stringify(value);
    }
  });
  // brandImages 는 서버가 KAPI 업로드 결과를 캐시하는 전용 필드다. 클라가 되돌려 쓰면 안 된다.
  delete campaign.brandImages;
  return campaign;
};

class API {
  constructor() {
    this._subscribers = {};
    // saga가 call(API.메서드,...)로 메서드를 떼어 넘기면 this 바인딩이 끊긴다
    // (배송주문 메서드의 this._callDeliveryField가 null → 에러). 전 메서드를 인스턴스에 바인딩해 고정.
    // (this 안 쓰는 메서드도 bind는 무해 — minify에도 안전하게 소스매칭 대신 전량 바인딩.)
    const proto = Object.getPrototypeOf(this);
    for (const name of Object.getOwnPropertyNames(proto)) {
      if (name === "constructor") continue;
      if (typeof proto[name] === "function") {
        this[name] = proto[name].bind(this);
      }
    }
  }

  sendCode(phone) {
    const body = { phone: phone };
    return new Promise(function (resolve, reject) {
      fetch(`${Constant.SERVER_URL}/code`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          if (res.status !== 200) {
            reject(new Error("Failed send code"));
          } else {
            resolve(res);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  confirmCode(phone, code) {
    return new Promise(function (resolve, reject) {
      fetch(`${Constant.SERVER_URL}/code?phone=${phone}&code=${code}`, {
        method: "GET",
      })
        .then((res) => {
          if (res.status !== 200) {
            reject(new Error("Invalid code"));
          } else {
            resolve(res);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getBillingCost(storeId, locationId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getBillingCost, {
          storeId,
          locationId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getBillingCost));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateItems(items) {
    delete items.legacy;
    delete items.updatedAt;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateItems, {
          input: items,
        }),
      )
        .then((data) => {
          resolve(data.data.updateItems);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createInventoryHistory(history) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createInventoryHistory, {
          input: history,
        }),
      )
        .then((data) => {
          resolve(data.data.createInventoryHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryHistories(storeId, page, size, type, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getInventoryHistories, {
          storeId,
          page,
          size,
          type,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getInventoryHistories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryHistoriesById(id, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listInventoryHistorys, {
          id,
          nextToken,
          sortDirection: "DESC",
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listInventoryHistorys);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryHistoriesByStore(storeId, createdAt, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listInventoryHistorysByStore, {
          storeId,
          createdAt,
          nextToken,
          sortDirection: "DESC",
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listInventoryHistorysByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryHistoriesByLocation(locationId, createdAt, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listInventoryHistorysByLocation, {
          locationId,
          createdAt,
          nextToken,
          sortDirection: "DESC",
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listInventoryHistorysByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSkuInventoryHistories(
    start,
    end,
    skuId,
    storeId,
    filters,
    nextToken,
    limit,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getSkuInventoryHistories, {
          start,
          end,
          skuId,
          storeId,
          filters: filters ? JSON.stringify(filters) : null,
          nextToken,
          sortDirection: "DESC",
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getSkuInventoryHistories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryHistorySummary(storeId, start, end, filters, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getInventoryHistorySummary, {
          storeId,
          start,
          end,
          filters: filters ? JSON.stringify(filters) : null,
          nextToken,
          sortDirection: "DESC",
          limit,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getInventoryHistorySummary));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrderWithTransactions(orderId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getOrder, {
          id: orderId,
        }),
      )
        .then((data) => {
          resolve(data.data.getOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrder(orderId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.getOrder, {
          id: orderId,
        }),
      )
        .then((data) => {
          resolve(data.data.getOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrderByApiKey(orderId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(custom.getOrder, {
          id: orderId,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrdersByLocation(orderLocationId, createdAt, nextToken, limit, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrdersByLocation, {
          orderLocationId,
          createdAt,
          limit,
          nextToken,
          filter,
          sortDirection: "DESC",
        }),
      )
        .then((data) => {
          resolve(data.data.listOrdersByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrdersByStore(storeId, createdAt, nextToken, limit, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        // TODO: optimzing by custom
        graphqlOperation(queries.listOrdersByStore, {
          storeId,
          createdAt,
          limit,
          nextToken,
          filter,
          sortDirection: "DESC",
        }),
      )
        .then((data) => {
          resolve(data.data.listOrdersByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getShippingOrdersByShippingLocation(
    shippingLocationId,
    createdAt,
    nextToken,
    limit,
    filter,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listOrdersByShippingLocation, {
          shippingLocationId,
          createdAt,
          sortDirection: "DESC",
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listOrdersByShippingLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getShippingDeltaOrdersByShippingLocation(
    shippingLocationId,
    createdAt,
    nextToken,
    limit,
    filter,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listOrderDeltaByShippingLocation, {
          shippingLocationId,
          createdAt,
          sortDirection: "DESC",
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listOrderDeltaByShippingLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrdersOnlyByStore(storeId, createdAt, nextToken, limit, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrdersByStore, {
          storeId,
          createdAt,
          limit,
          nextToken,
          filter,
          sortDirection: "DESC",
        }),
      )
        .then((data) => {
          resolve(data.data.listOrdersByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrdersByStoreCustomer(
    orderCustomerId,
    filter = null,
    limit = null,
    nextToken = null,
    start = null,
    end = null,
  ) {
    console.log("API.getOrders");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrdersByStoreCustomer, {
          orderCustomerId: orderCustomerId,
          sortDirection: "DESC",
          createdAt: start && end ? { between: [start, end] } : null,
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listOrdersByStoreCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrdersByCustomer(customerId, nextToken, limit, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrdersByCustomer, {
          customerId,
          nextToken,
          limit,
          filter,
          sortDirection: "DESC",
        }),
      )
        .then((data) => {
          resolve(data.data.listOrdersByCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderCustomerGroups(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderCustomerGroups, {
          storeId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProviderCustomerGroups));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // 그랑핸드 앱 카탈로그 싱크 2단계 — 발급받은 presigned URL 로 sales-server 가 카탈로그 rows 를 업로드한다
  exportProviderCatalog(storeId, uploadUrl, eventId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_exportProviderCatalog, {
          storeId,
          uploadUrl,
          eventId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_exportProviderCatalog);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // 자사몰(카페24)의 shop = 서브몰 목록. 채널 설정의 서브몰 선택 모달에서만 호출한다.
  // ★ 이 리졸버는 python 쪽에서 json.dumps(...) 한 "문자열"을 return 한다. AWSJSON 이 그걸 한 번 더
  //   감싸므로 응답이 이중 인코딩되어 JSON.parse 를 두 번 해야 배열이 나온다.
  //   (dict/list 를 그대로 return 하는 LS_getProviderCustomerGroups 는 1회면 된다.)
  //   서버가 나중에 list 를 그대로 return 하도록 바뀌어도 동작하게 "문자열이 아닐 때까지" 푼다.
  listAvailableShops(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_listAvailableShops, {
          storeId,
        }),
      )
        .then((data) => {
          let parsed = data?.data?.LS_listAvailableShops ?? null;
          // 인코딩 깊이가 바뀌어도 견디도록 최대 3회까지만 푼다 (무한루프 방지).
          for (let i = 0; i < 3 && typeof parsed === "string"; i += 1) {
            try {
              parsed = JSON.parse(parsed);
            } catch (e) {
              break;
            }
          }
          resolve(parsed);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderOrders(storeId, page, size, filters) {
    console.log("API.getProviderOrders");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderOrders, {
          storeId,
          page,
          size,
          filters: JSON.stringify(filters),
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProviderOrders));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getTierUpdateHistories(storeId, createdAt, nextToken, limit, filter = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCustomerTierUpdateHistoryByStore, {
          storeId,
          createdAt,
          sortDirection: "DESC",
          filter: filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listCustomerTierUpdateHistoryByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrganizationsByEmployee(employeeId, nextToken, limit, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrganizationsByEmployee, {
          employeeId,
          nextToken,
          limit,
          filter,
        }),
      )
        .then((data) => {
          console.log(data);
          resolve(data.data.listOrganizationsByEmployee);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrganizationsByStore(storeId, nextToken, limit, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrganizationsByStore, {
          storeId,
          nextToken,
          limit,
          filter,
          sortDirection: "ASC",
        }),
      )
        .then((data) => {
          resolve(data.data.listOrganizationsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrganizationsByLocation(locationId, nextToken, limit, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrganizationsByLocation, {
          locationId,
          nextToken,
          limit,
          filter,
          sortDirection: "ASC",
        }),
      )
        .then((data) => {
          resolve(data.data.listOrganizationsByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrders(storeId, page, size, filters = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getOrders, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getOrders);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrdersStats(storeId, filters = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getOrdersStats, {
          storeId,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getOrdersStats));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  echo(message) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.echo, { message }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.echo);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  resetPassword(ident, password) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.resetPassword, { ident, password }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.resetPassword);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  generateAuthCode(redirectUrl, customize) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.generateAuthCode, {
          redirectUrl,
          customize,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.generateAuthCode);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  decodeAuthData(encData) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.decodeAuthData, { data: encData }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(JSON.parse(data.data.decodeAuthData));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createUser(ident, firstName, lastName, phone, storeName, resend = false) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.createUser, {
          ident,
          firstName,
          lastName,
          phone,
          storeName,
          resend,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.createUser));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteUser(ident) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.deleteUser, {
          ident,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.deleteUser));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPointStatsByCustomerId(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getPointStatsByCustomerId, { id }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(JSON.parse(data.data.getPointStatsByCustomerId));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerByStore(customerId, storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(custom.listStoreByCustomer, {
          customerId,
          storeId: { eq: storeId },
          limit: 1,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          const { items } = data.data.listStoreByCustomer;
          resolve(items.length > 0 ? items[0] : null);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationCustomersByOriginId(
    customerId,
    storeId = null,
    nextToken = null,
    limit = null,
    filter = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.listLocationCustomerDeltaByCustomer, {
          customerId: customerId,
          storeId: storeId,
          nextToken: nextToken,
          limit: limit,
          filter: filter,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.listLocationCustomerDeltaByCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationCustomer(id, locationId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getLocationCustomerDelta, {
          id,
          locationId,
        }),
      )
        .then((data) => {
          resolve(data.data.getLocationCustomerDelta);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationCustomerWithStoreCustomer(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.listLocationCustomerDeltas, {
          id,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          const { items } = data.data.listLocationCustomerDeltas;
          resolve(items.length > 0 ? items[0] : null);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationCustomersByStore(
    storeId,
    timeStamp = null,
    limit,
    filter = null,
    nextToken = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationCustomerDeltaByStore, {
          storeId,
          timeStamp,
          sortDirection: "DESC",
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationCustomerDeltaByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationCustomersByLocation(
    locationId,
    timeStamp = null,
    limit,
    filter = null,
    nextToken = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationCustomerDeltaByLocation, {
          locationId,
          timeStamp,
          sortDirection: "DESC",
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationCustomerDeltaByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createInventory(inventory) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.createSkuInventory, {
          inventory: JSON.stringify(inventory),
        }),
      )
        .then((data) => {
          resolve(data.data.createInventory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateInventory(inventory) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateSkuInventory, {
          inventory: JSON.stringify(inventory),
        }),
      )
        .then((data) => {
          resolve(data.data.deleteInventory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteInventory(inventory) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteSkuInventory, {
          inventory: JSON.stringify(inventory),
        }),
      )
        .then((data) => {
          resolve(data.data.deleteInventory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelCardPay(ident, transactionId, amount) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.cancelCardPay, {
          ident,
          transactionId,
          amount,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.cancelCardPay));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getKakaoCategories() {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(graphqlOperation(queries.getKakaoCategories))
        .then((data) => {
          resolve(JSON.parse(data.data.getKakaoCategories));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  requestKakaoProfileToken(channelId, phone) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_requestKakaoProfileToken, {
          channelId,
          phone,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_requestKakaoProfileToken);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  registerKakaoProfile(channelId, token, phone, categoryCode) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_registerKakaoProfile, {
          channelId,
          token,
          phone,
          categoryCode,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_registerKakaoProfile);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // 발신프로필 라이브 조회(다우가 단일 소스). store.kakao 는 "연동했는가"만 알고,
  // 상태/브랜드메시지 승인 여부는 여기서만 확인된다.
  // 다우 응답에 없는 키는 null 이 아니라 아예 생략된다(server sales.py:4657) —
  // 호출부는 undefined 를 "미확인"으로 다루고 `=== true` / `!!` 로 판정할 것.
  getKakaoProfile(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(graphqlOperation(queries.LS_getKakaoProfile, { storeId }))
        .then((data) => {
          const raw = data.data.LS_getKakaoProfile;
          resolve(typeof raw === "string" ? JSON.parse(raw) : raw);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // 브랜드메시지(M/N/O) 사용 신청. 서버는 인자 유무와 무관하게 apply 를 항상 호출하므로
  // (server sales.py:4683) "080 번호만 수정" 용도로 재호출하면 재신청이 된다.
  // evidence 는 { key, level } — Storage.put 결과. level 미지정 시 서버가 "public" 을 쓴다.
  // 응답: { unsubscribe?, evidence?, apply, profile }
  applyBrandMessage(
    storeId,
    unsubscribePhoneNumber = null,
    unsubscribeAuthNumber = null,
    evidence = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_applyBrandMessage, {
          storeId,
          unsubscribePhoneNumber,
          unsubscribeAuthNumber,
          evidence: evidence ? JSON.stringify(evidence) : null,
        }),
      )
        .then((data) => {
          const raw = data.data.LS_applyBrandMessage;
          resolve(typeof raw === "string" ? JSON.parse(raw) : raw);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getAlimtalkTemplates(channelId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getAlimtalkTemplates, {
          channelId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getAlimtalkTemplates));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelCashReceipt(
    ident,
    transactionId,
    amount,
    supplyAmount,
    vat,
    taxFreeAmount,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.cancelCashReceipt, {
          ident,
          transactionId,
          amount,
          supplyAmount,
          vat,
          taxFreeAmount,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.cancelCashReceipt));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  decodeToken(token) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.decodeToken, { token }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(JSON.parse(data.data.decodeToken));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSettlements(
    locationId,
    transactionDate,
    limit,
    sortDirection,
    nextToken = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationSettlements, {
          locationId,
          transactionDate,
          limit,
          sortDirection,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationSettlements);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSettlementsByStore(
    storeId,
    transactionDate,
    limit,
    sortDirection,
    nextToken = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationSettlementByStore, {
          storeId,
          transactionDate,
          limit,
          sortDirection,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationSettlementByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSalesHistoriesByStore(storeId, start, end = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listSalesHistorys, {
          storeId,
          limit: 500,
          sortDirection: "DESC",
          startedAt: {
            between: [start, end || new Date().toISOString()],
          },
        }),
      )
        .then((data) => {
          resolve(data.data.listSalesHistorys.items);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSalesHistoriesByLocation(locationId, start, end = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listSalesHistorysByLocation, {
          locationId,
          sortDirection: "DESC",
          limit: 500,
          filter: {
            startedAt: {
              between: [start, end || new Date().toISOString()],
            },
          },
        }),
      )
        .then((data) => {
          resolve(data.data.listSalesHistorysByLocation.items);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrderHistoriesByStore(storeId, start, end, filters, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrderHistorys, {
          storeId,
          createdAt: {
            between: [start, end],
          },
          filter: filters ? filters : null,
          sortDirection: "DESC",
          limit: limit,
          nextToken: nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listOrderHistorys);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrderHistoriesByLocation(locationId, start, end, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listOrderHistorysByLocation, {
          locationId,
          createdAt: {
            between: [start, end],
          },
          sortDirection: "DESC",
          limit: limit,
          nextToken: nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listOrderHistorysByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSalesSummary(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getSalesSummary, {
          id: storeId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getSalesSummary));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPointTransactions(nextToken, limit, filter = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listPointTransactions, {
          filter: filter || {},
          nextToken: nextToken,
          limit: limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listPointTransactions);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPointTransactionsByStore(
    storeId,
    createdAt = {},
    nextToken = null,
    limit = null,
    filter = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listPointTransactionsByStore, {
          storeId,
          createdAt,
          sortDirection: "DESC",
          limit,
          nextToken,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listPointTransactionsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPointTransactionsByStoreSalesDate(
    storeId,
    salesDate = null,
    nextToken = null,
    limit = null,
    filter = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listPointTransactionsByStoreSalesDate, {
          storeId,
          salesDate,
          sortDirection: "DESC",
          limit,
          nextToken,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listPointTransactionsByStoreSalesDate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPointTransactionsByLocation(
    locationId,
    createdAt = {},
    nextToken = null,
    limit = null,
    filter = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listPointTransactionsByLocation, {
          locationId,
          createdAt,
          sortDirection: "DESC",
          limit,
          nextToken,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listPointTransactionsByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPointTransactionsByLocationSalesDate(
    locationId,
    salesDate = {},
    nextToken = null,
    limit = null,
    filter = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listPointTransactionsByLocationSalesDate, {
          locationId,
          salesDate,
          sortDirection: "DESC",
          limit,
          nextToken,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listPointTransactionsByLocationSalesDate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCreditTransactionsByStore(
    storeId,
    createdAt = {},
    nextToken = null,
    limit = null,
    filter = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listCreditTransactionsByStore, {
          storeId,
          createdAt,
          sortDirection: "DESC",
          limit,
          nextToken,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listCreditTransactionsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPointTransactionByStoreCustomer(
    storeCustomerId,
    filter = null,
    limit = null,
    nextToken = null,
    start = null,
    end = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listPointTransactionsByStoreCustomer, {
          pointTransactionCustomerId: storeCustomerId,
          sortDirection: "DESC",
          createdAt: start && end ? { between: [start, end] } : null,
          filter: filter,
          limit: limit,
          nextToken: nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listPointTransactionsByStoreCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderCustomerPointTransactions(storeId, customerId, start, end) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderPointTransactions, {
          storeId,
          customerId,
          start,
          end,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProviderPointTransactions));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCreditTransactionsByCustomer(customerId, nextToken, filter, limit = 300) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listCreditTransactionsByCustomer, {
          customerId,
          nextToken,
          limit,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listCreditTransactionsByCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCreditTransactionsByStoreCustomer(
    storeCustomerId,
    nextToken = null,
    filter = null,
    limit = null,
    start = null,
    end = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listCreditTransactionsByStoreCustomer, {
          creditTransactionCustomerId: storeCustomerId,
          sortDirection: "DESC",
          filter: !_.isEmpty(filter) ? filter : null,
          limit: limit,
          nextToken: nextToken,
          createdAt: start && end ? { between: [start, end] } : null,
        }),
      )
        .then((data) => {
          resolve(data.data.listCreditTransactionsByStoreCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCreditTransactions(nextToken, limit, filter = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCreditTransactions, {
          filter: filter || {},
          nextToken: nextToken,
          limit: limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listCreditTransactions);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSalesReport(
    storeId,
    item,
    start,
    end,
    locationId,
    locationIds,
    page = null,
    totalPage = null,
    basis,
    brandId = null,
    filters = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        // filters 는 서버 배포 후 queries.js 가 pull 로 갱신되기 전까지는
        // 쿼리에 선언되지 않은 변수라 서버에 전달되지 않음 (무해하게 무시됨).
        graphqlOperation(queries.getSalesReport, {
          id: storeId,
          item,
          start,
          end,
          locationId,
          locationIds,
          brandId,
          basis,
          page,
          totalPage,
          filters: filters ? JSON.stringify(filters) : null,
          version: REPORT_API_VERSION,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getSalesReport));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // options: 계약 §1-0 의 v2 신규 인자 묶음 (compare/mode/page/sort/q/…).
  // 🔴 조립·직렬화는 이 메서드 한 곳에서만 한다 (AWSJSON = JSON 문자열).
  //    queries.js 에 options 가 선언되기 전까지는 서버로 전달되지 않고 무시된다
  //    (filters 와 동일한 기존 사례 — 위 getSalesReport 주석 참조).
  getPeriodReport(
    storeId,
    item,
    start,
    end,
    locationId = null,
    basis = null,
    origin = null,
    brandId = null,
    options = null,
  ) {
    // v2 테스트 스위치가 켜졌고 v2 대상 item 일 때만 게이트 통과 버전을 보낸다.
    // off(운영) 면 기존 버전 그대로 → 서버는 기존 응답을 내려준다.
    const version = isReportV2(item)
      ? REPORT_V2_TEST_VERSION
      : REPORT_API_VERSION;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getPeriodReport, {
          storeId,
          locationId,
          brandId,
          item,
          start,
          end,
          basis,
          origin,
          version,
          // 🔴 `__sectionRetry` 는 웹 내부 표식이다 — 서버 화이트리스트(§1-0)에
          //    없어 그대로 보내면 **미지 키 에러로 조회 전체가 깨진다**. 여기서 뗀다.
          ...(IS_REPORT_V2_TEST && options
            ? {
                options: JSON.stringify(
                  Object.fromEntries(
                    Object.entries(options).filter(([k]) => !k.startsWith("__")),
                  ),
                ),
              }
            : null),
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getPeriodReport));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // 리포트 추출 (계약 §12) — 조회와 다른 엔드포인트. page/size 를 받지 않는다(항상 전량).
  // sort·q 는 화면 상태 그대로 options 로 실어 보낸다. detail 은 제품 리포트 전용.
  getPeriodReportExport(storeId, item, section, start, end, options = null) {
    const version = isReportV2(item)
      ? REPORT_V2_TEST_VERSION
      : REPORT_API_VERSION;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getPeriodReportExport, {
          storeId,
          item,
          section,
          start,
          end,
          version,
          ...(IS_REPORT_V2_TEST && options
            ? { options: JSON.stringify(options) }
            : null),
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getPeriodReportExport));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getMembershipReport(storeId, start, end, interval) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getMembershipReport, {
          storeId,
          start,
          end,
          interval,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getMembershipReport));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  validateImage(key, method, level, options) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_validateImage, {
          key,
          method,
          level,
          options: options ? JSON.stringify(options) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_validateImage);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerCount(
    storeId,
    locationId = null,
    status = null,
    filters = null,
    includes = [],
    locationIds = [],
    version = "1.0",
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCustomerCount, {
          storeId,
          locationId,
          locationIds,
          status,
          version,
          filters: filters ? JSON.stringify(filters) : null,
          includes,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getCustomerCount);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerGroups(
    storeId,
    locationId = null,
    page = null,
    size = null,
    group = "all",
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCustomerGroups, {
          storeId,
          locationId,
          page,
          size,
          group,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerGroups));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerGroupsWithStats(storeId, locationId, page, maxPage) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCustomerGroupWithStats, {
          id: storeId,
          locationId,
          page,
          maxPage,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.listCustomerGroupWithStats));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLoyaltySummary(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getLoyaltySummary, {
          id: storeId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getLoyaltySummary));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCampaignWithReport(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getCampaignWithReport, {
          id: id,
        }),
      )
        .then((data) => {
          let campaign = data.data.getCampaignWithReport;
          if (typeof campaign.report === "string") {
            campaign = {
              ...campaign,
              report: JSON.parse(campaign.report),
            };
          }
          resolve(parseBrandCampaign(campaign));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  generateStore(employee, survey, settings = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.generateStore, {
          employee: JSON.stringify(employee),
          survey: JSON.stringify(survey),
          settings: settings ? JSON.stringify(settings) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.generateStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCardBillingKey(storeId, cardNo, cardPw, expMonth, expYear, identify) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.generateBillingKey, {
          storeId: storeId,
          cardNo: cardNo,
          cardPw: cardPw,
          expMonth: expMonth,
          expYear: expYear,
          identify: identify,
        }),
      )
        .then((data) => {
          resolve(data.data.generateBillingKey);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelCampaign(campaignId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.cancelCampaign, {
          id: campaignId,
        }),
      )
        .then((data) => {
          resolve(data.data.cancelCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  launchCampaign(campaignId, recipientIds) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.launchCampaign, {
          id: campaignId,
          recipientIds: recipientIds,
        }),
      )
        .then((data) => {
          resolve(parseBrandCampaign(data.data.launchCampaign));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // options 는 method 별 자유 dict 로 서버에 전달된다.
  // brandmessage 테스트 발송은 여기로만 간다 —
  // { kakao:{key}, messageType, sendTarget, channelName, image:{url:<S3 key>,link},
  //   listItems, cards, tail, buttons, couponId }.
  // ★ 저장 shape 은 {key,level} 인데 테스트 발송은 {url:<S3 key>} 이고 level 은 서버가
  //   'public' 으로 하드코딩한다(server sales.py:465). 변환 없이 그대로 넘기면 안 된다.
  // gotcha: 6번째 인자 trackingLink 는 문서 변수 $originUrl 과 이름이 달라 서버에 도달하지 않는다.
  //   (현재 호출부 전부 null 이라 무해. 시그니처 정리는 별건)
  sendSms(
    message,
    method,
    recipient,
    title,
    sendNumber,
    trackingLink,
    imageUrl,
    options,
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.sendSms, {
          message: message,
          method: method,
          recipient: recipient,
          title: title,
          sendNumber: sendNumber,
          trackingLink,
          imageUrl,
          options: options ? JSON.stringify(options) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.sendSms);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCoupon(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getCoupon, {
          id: id,
        }),
      )
        .then((data) => {
          resolve(data.data.getCoupon);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponByApiKey(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getCoupon, {
          id: id,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getCoupon);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCoupons(storeId, nextToken, limit, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCouponsByStore, {
          storeId,
          nextToken,
          limit,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listCouponsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponsWithStats(storeId, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCouponsWithStats, {
          storeId,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCouponsWithStats));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCoupon(coupon) {
    delete coupon.transactions;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_createCoupon, {
          coupon,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_createCoupon);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateCoupon(coupon) {
    delete coupon.transactions;
    delete coupon.discountAmount;
    delete coupon.locations;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_updateCoupon, {
          coupon,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_updateCoupon);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCoupon(couponId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deleteCoupon, {
          couponId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_deleteCoupon);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponTransactionByApiKey(transactionId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getCouponTransaction, {
          id: transactionId,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getCouponTransaction);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponTransactionsByStoreCoupon(
    storeId,
    couponId,
    filter,
    limit,
    nextToken,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCouponTransactionsByStoreCoupon, {
          storeId,
          filter,
          limit,
          nextToken,
          couponTransactionsId: couponId ? { eq: couponId } : null,
        }),
      )
        .then((data) => {
          console.log(data);
          resolve(data.data.listCouponTransactionsByStoreCoupon);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponTransactionsByStoreCustomer(
    customerId,
    method,
    filter,
    limit,
    nextToken,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCouponTransactionsByStoreCustomerMethod, {
          couponTransactionCustomerId: customerId,
          method: method ? { eq: method } : null,
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          console.log(data);
          resolve(data.data.listCouponTransactionsByStoreCustomerMethod);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderCouponTransactions(storeId, customerId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderCouponTransactions, {
          storeId,
          customerId,
        }),
      )
        .then((data) => {
          resolve(
            JSON.parse(data.data.LS_getProviderCouponTransactions).items || [],
          );
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponTransactions(
    storeId,
    start,
    end,
    { locationId, couponId, customerId, page, size } = {},
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCouponTransactions, {
          storeId,
          start,
          end,
          locationId: locationId || null,
          couponId: couponId || null,
          customerId: customerId || null,
          page: page || null,
          size: size || null,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCouponTransactions));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderCouponIssues(
    storeId,
    couponId,
    { start, end, providerCustomerId } = {},
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderCouponIssues, {
          storeId,
          couponId,
          start: start || null,
          end: end || null,
          providerCustomerId: providerCustomerId || null,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProviderCouponIssues));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createProviderCouponTransaction(storeId, customerId, couponId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_createProviderCouponTransaction, {
          storeId,
          customerId,
          couponId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_createProviderCouponTransaction);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponTransactionsByStoreCustomerByApi(
    couponId,
    customerId,
    filter,
    limit,
    nextToken,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(
          queries.listCouponTransactionsByCouponStoreCustomer,
          {
            filter,
            limit,
            nextToken,
            couponTransactionsId: couponId,
            couponTransactionCustomerId: customerId ? { eq: customerId } : null,
          },
        ),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          console.log(data);
          resolve(data.data.listCouponTransactionsByCouponStoreCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponTransactionsByStoreBarcode(
    storeId,
    barcode,
    filter,
    limit,
    nextToken,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.listCouponTransactionsByStoreBarcode, {
          storeId,
          filter,
          limit,
          nextToken,
          barcode: barcode ? { eq: barcode } : null,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          console.log(data);
          resolve(data.data.listCouponTransactionsByStoreBarcode);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCampaign(campaignId) {
    return new Promise(function (resolve, reject) {
      // 문서 변수는 $id 다(queries.getCampaign). campaignId 로 넘기면 ID! 가 미바인딩돼 항상 실패한다.
      AWSAPI.graphql(
        graphqlOperation(queries.getCampaign, {
          id: campaignId,
        }),
      )
        .then((data) => {
          resolve(parseBrandCampaign(data.data.getCampaign));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCampaignsByStoreId(
    storeId,
    createdAt = null,
    filter = null,
    nextToken = null,
    limit = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCampaignsByStore, {
          storeId,
          createdAt,
          filter,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(parseBrandCampaignList(data.data.listCampaignsByStore));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCampaignsByLocationId(
    locationId,
    createdAt = null,
    filter = null,
    nextToken = null,
    limit = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCampaignsByLocation, {
          locationId,
          createdAt,
          filter,
          nextToken,
          limit,
          sortDirection: "DESC",
        }),
      )
        .then((data) => {
          resolve(parseBrandCampaignList(data.data.listCampaignsByLocation));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getBillingHistory(billHistoryId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getBillHistory, {
          id: billHistoryId,
        }),
      )
        .then((data) => {
          resolve(data.data.getBillHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getBillingHistoriesByStore(
    storeId,
    filter = null,
    limit = null,
    nextToken = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listBillHistoryByStore, {
          storeId,
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listBillHistoryByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getBillingHistoriesByLocation(
    locationId,
    filter = null,
    limit = null,
    nextToken = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listBillHistoryByLocation, {
          locationId,
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listBillHistoryByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /**
   * BillHistory byType 인덱스 조회 (가맹점 관리 > provider 결제내역).
   *
   * @param {string} type - BillHistory.type ("cafe24" | "imweb" | ...)
   * @param {object|null} date - ModelStringKeyConditionInput. null 이면 전체 구간.
   * @param {string} sortDirection - "DESC" 면 최신순.
   */
  getBillingHistoriesByType(
    type,
    date = null,
    filter = null,
    limit = null,
    nextToken = null,
    sortDirection = "DESC",
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listBillHistoryByTypeSummary, {
          type,
          date,
          sortDirection,
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listBillHistoryByType);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /** provider 계약 목록 (경량). filter 는 호출부에서 cafe24/imweb or 조건으로 전달. */
  getProviderStoreContracts(limit = null, nextToken = null, filter = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listProviderStoreContracts, {
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listStoreContracts);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /** 계정 이름 + policy.provider 만 내려받는 경량 getStore. */
  getStoreProviderSummary(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.getStoreProviderSummary, { id: storeId }),
      )
        .then((data) => {
          resolve(data.data.getStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createEmployee(employee) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.createEmployee, { input: employee }),
      })
        .then((data) => {
          resolve(data.data.createEmployee);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateEmployee(employee) {
    delete employee.store;
    delete employee.organizations;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateEmployee, {
          input: employee,
        }),
      )
        .then((data) => {
          resolve(data.data.updateEmployee);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteEmployee(employeeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteEmployee, {
          input: {
            id: employeeId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteEmployee);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSubscriptionPlans(storeId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listSubscriptionPlanDeltaByStore, {
          storeId,
          nextToken,
          limit,
          filter: { _delta: { ne: "DELETE" }, status: { ne: "archived" } },
        }),
      )
        .then((data) => {
          resolve(data.data.listSubscriptionPlanDeltaByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSubscriptionPlan(planId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getSubscriptionPlanDelta, {
          id: planId,
        }),
      )
        .then((data) => {
          resolve(data.data.getSubscriptionPlanDelta);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createSubscriptionPlan(plan) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_createSubscriptionPlan, {
          plan: JSON.stringify(plan),
        }),
      )
        .then((data) => {
          resolve(data.data.LS_createSubscriptionPlan);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateSubscriptionPlan(plan) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_updateSubscriptionPlan, {
          plan: JSON.stringify(plan),
        }),
      )
        .then((data) => {
          resolve(data.data.LS_updateSubscriptionPlan);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCustomerPayment(payment) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_createCustomerPayment, {
          payment: JSON.stringify(payment),
        }),
      )
        .then((data) => {
          resolve(data.data.LS_createCustomerPayment);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCustomerPayment(id) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deleteCustomerPayment, {
          paymentId: id,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_deleteCustomerPayment);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  subscribeSubscriptionPlan(customerId, planId, locationId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_subscribeSubscriptionPlan, {
          customerId,
          planId,
          locationId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_subscribeSubscriptionPlan);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  stopSubscription(subscriptionId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_stopSubscription, {
          subscriptionId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_stopSubscription);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  restoreSubscription(subscriptionId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_restoreSubscription, {
          subscriptionId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_restoreSubscription);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelSubscription(subscriptionId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_cancelSubscription, {
          subscriptionId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_cancelSubscription);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSubscriptionEvents(customerId, limit, nextToken) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(custom.listSubscriptionEvents, {
          filter: { customerId: { eq: customerId }, type: { ne: "failed" } },
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listSubscriptionEvents);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteSubscriptionPlan(planId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deleteSubscriptionPlan, {
          planId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_deleteSubscriptionPlan);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getReviewCampaigns(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getReviewCampaigns, {
          storeId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getReviewCampaigns));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getReviewCampaignResult(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getReviewCampaignResult, {
          id,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getReviewCampaignResult);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getReviewCampaignResults(storeId, start, end) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getReviewCampaignResults, {
          storeId,
          start,
          end,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getReviewCampaignResults));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getReviewCampaignResultsByCapaign(campaignId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listReviewCampaignResults, {
          filter: { campaignId: { eq: campaignId } },
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listReviewCampaignResults);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateReviewCampaignResult(result) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.updateReviewCampaignResult, {
          input: result,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.updateReviewCampaignResult);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getReviewCampaign(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getReviewCampaign, {
          id,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getReviewCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrdersByReciptCustomer(customerId, limit = null, nextToken = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listOrdersByReceiptCustomer, {
          receiptCustomerId: customerId,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listOrdersByReceiptCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createReviewCampaign(campaign) {
    delete campaign.stats;
    if (typeof campaign.questions !== "string") {
      campaign.questions = JSON.stringify(campaign.questions);
    }
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createReviewCampaign, {
          input: campaign,
        }),
      )
        .then((data) => {
          resolve(data.data.createReviewCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateReviewCampaign(campaign) {
    delete campaign.stats;
    if (typeof campaign.questions !== "string") {
      campaign.questions = JSON.stringify(campaign.questions);
    }
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateReviewCampaign, {
          input: campaign,
        }),
      )
        .then((data) => {
          resolve(data.data.updateReviewCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteReviewCampaign(campaignId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteReviewCampaign, {
          input: { id: campaignId },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteReviewCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCampaign(campaign) {
    delete campaign.recipient;
    delete campaign.recipientTag;
    delete campaign.report;
    delete campaign.results;
    if (!campaign.recipientId) {
      delete campaign.recipientId;
    }
    if (!campaign.status) {
      delete campaign.status;
    }
    if (!campaign.locationId) {
      delete campaign.locationId;
    }
    if (!campaign.storeId) {
      delete campaign.storeId;
    }
    serializeBrandCampaign(campaign);
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createCampaign, {
          input: campaign,
        }),
      )
        .then((data) => {
          resolve(parseBrandCampaign(data.data.createCampaign));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateCampaign(campaign) {
    delete campaign.recipient;
    delete campaign.recipientTag;
    delete campaign.report;
    delete campaign.results;
    if (!campaign.recipientId) {
      delete campaign.recipientId;
    }
    if (!campaign.status) {
      delete campaign.status;
    }
    if (!campaign.locationId) {
      delete campaign.locationId;
    }
    if (!campaign.storeId) {
      delete campaign.storeId;
    }
    serializeBrandCampaign(campaign);
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateCampaign, {
          input: campaign,
        }),
      )
        .then((data) => {
          resolve(parseBrandCampaign(data.data.updateCampaign));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCampaign(campaignId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteCampaign, {
          input: {
            id: campaignId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProductCategories(storeId, nextToken = null, limit = null) {
    console.log("getProductCategories");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listProductCategoryByStore, {
          storeId,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listProductCategoryByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProductModifiers(storeId, nextToken = null, limit = null) {
    console.log("getProductModifiers");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listProductModifierByStore, {
          storeId,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listProductModifierByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProductsByApiKey(storeId, limit = 1000) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(custom.listProductsByStore, {
          storeId,
          limit,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.listProductsByStore?.items || []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomer(customerId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getCustomer, {
          customerId,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomer));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreCustomer(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.getStoreCustomer, {
          id: id,
        }),
      )
        .then((data) => {
          resolve(data.data.getStoreCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreCustomerWithTransactions(id, customerId, nextToken, limit = 300) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listCustomerByStoreWithTransactions, {
          storeId: id,
          customerId: { eq: customerId },
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listCustomerByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createStoreProducts(storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.createStoreProducts, {
          input: { id: storeId },
        }),
      )
        .then((data) => {
          resolve(data.data.createStoreProducts);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  static pickCustomer(customer) {
    // TODO: use scheme, not hard coding
    return _.pick(customer, [
      "id",
      "enterpriseId",
      "storeId",
      "locationId",
      "customerId",
      "customerCreatedAt",
      "ident",
      "identType",
      "attributes",
      "type",
      "name",
      "addresses",
      "phone",
      "email",
      "birth",
      "agreements",
      "ageGroup",
      "gender",
      "status",
      "registeredAt",
      "campaignBlockedAt",
      "isFavorite",
      "locations",
      "note",
      "tags",
      "sources",
      "removedAt",
      "updatedAt",
      "createdAt",
      "_delta",
      "timestamp",
      "expdate",
    ]);
  }

  createCustomer(customer, profileOnly) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_createCustomer, {
          customer: JSON.stringify(API.pickCustomer(customer)),
          profileOnly,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.LS_createCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateCustomer(customer, profileOnly = false) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_updateCustomer, {
          customer: JSON.stringify(API.pickCustomer(customer)),
          profileOnly,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.LS_updateCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCustomer(customerId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deleteCustomer, {
          customerId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_deleteCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomers(
    customerId = null,
    storeId = null,
    locationId = null,
    status = null,
    withStore = true,
    page = null,
    size = null,
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getCustomers, {
          customerId,
          storeId,
          locationId,
          status,
          withStore,
          page,
          size,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomers));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomersWithCount(
    storeId,
    page,
    size,
    sort,
    filters,
    search,
    locationId,
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getCustomersWithCount, {
          storeId,
          page,
          size,
          sort,
          search,
          filters: JSON.stringify(filters),
          extraSize: 1,
          locationId: locationId,
        }),
      })
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomersWithCount));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerList(storeId, page, size, sort, filters, search, locationId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getCustomerList, {
          storeId,
          page,
          size,
          sort,
          search,
          filters: JSON.stringify(filters),
          extraSize: 1,
          locationId: locationId,
        }),
      })
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerList));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerTagsWithCount(storeId, locationId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getCustomerTagsWithCount, {
          storeId,
          locationId,
        }),
      })
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerTagsWithCount));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerCountsByStatus(storeId, locationId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getCustomerCountsByStatus, {
          storeId,
          locationId,
        }),
      })
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerCountsByStatus));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /**
   * @return {obj} {id: , name:, phone:, lastVisitDate: }
   */
  getCustomersWithStats(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCustomersWithStats, {
          storeId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.listCustomersWithStats));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerWithStats(customerId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getCustomerWithStats, {
          customerId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getCustomerWithStats));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  sendReceipt(method, recipient, order) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.sendReceipt, {
          method: method,
          recipient: recipient,
          order: JSON.stringify(order),
        }),
      )
        .then((data) => {
          resolve(data.data.sendReceipt);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  sendReceiptLink(recipient, order) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.sendReceiptLink, {
          recipient: recipient,
          order: JSON.stringify(order),
        }),
      )
        .then((data) => {
          resolve(data.data.sendReceiptLink);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // getProducts

  getItems(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getItems, { id: storeId }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getItems);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getItem(productId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getProduct, { id: productId }),
      })
        .then((data) => {
          resolve(data.data.getProduct);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getItemByApiKey(productId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(custom.getProduct, { id: productId }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getProduct);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createPointTransaction(transaction) {
    console.log("API.createPointTransaction");
    if (!transaction.salesDate) {
      delete transaction.salesDate;
    }
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(custom.createPointTransaction, {
          input: transaction,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.createPointTransaction);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCreditTransaction(transaction) {
    console.log("API.createCreditTransaction");
    if (!transaction.salesDate) {
      delete transaction.salesDate;
    }
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(custom.createCreditTransaction, {
          input: transaction,
        }),
      )
        .then((data) => {
          resolve(data.data.createCreditTransaction);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createMessage(message) {
    if (message.action?.args) {
      message.action.args = JSON.stringify(message.action.args);
    }
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.createMessage, { input: message }),
      })
        .then((data) => {
          resolve(data.data.createMessage);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateInventoryHistory(historyId, quantity, cost, expiryDate) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_updateInventoryHistory, {
          historyId,
          quantity,
          cost,
          expiryDate,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_updateInventoryHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateInventoryHistoryMeta(historyId, note) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_updateInventoryHistoryMeta, {
          historyId,
          meta: JSON.stringify({ note: note }),
        }),
      )
        .then((data) => {
          resolve(data.data.LS_updateInventoryHistoryMeta);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  adjustInventories(adjustments) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_adjustInventories, {
          adjustments: adjustments,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_adjustInventories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelAdjusments(historyIds) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_cancelAdjustments, {
          historyIds,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_cancelAdjustments);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createInventoryRequestHistory(history) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_createInventoryRequestHistory, {
          history,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_createInventoryRequestHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createInventoryRequestHistories(histories) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_createInventoryRequestHistories, {
          histories,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_createInventoryRequestHistories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateInventoryRequestHistory(history) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_updateInventoryRequestHistory, {
          history,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_updateInventoryRequestHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateInventoryRequestHistories(histories) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_updateInventoryRequestHistories, {
          histories,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_updateInventoryRequestHistories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteInventoryRequestHistory(historyId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_deleteInventoryRequestHistory, {
          historyId,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_deleteInventoryRequestHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryRequestHistories(storeId, page, size, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getInventoryRequestHistories, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_getInventoryRequestHistories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryRequestHistory(historyId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getInventoryRequestHistory, {
          historyId,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_getInventoryRequestHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createInventoryTransferHistory(history) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_createInventoryTransferHistory, {
          history,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_createInventoryTransferHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createInventoryTransactions(adjustments) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_adjustInventories, {
          adjustments: adjustments,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_adjustInventories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateInventoryTransferHistory(history) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_updateInventoryTransferHistory, {
          history,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_updateInventoryTransferHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteInventoryTransferHistory(historyId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_deleteInventoryTransferHistory, {
          historyId,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_deleteInventoryTransferHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryTransferHistories(storeId, page, size, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.LS_getInventoryTransferHistories, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      })
        .then((data) => {
          resolve(data.data.LS_getInventoryTransferHistories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getInventoryCountHistories(storeId, page, size, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getInventoryCountHistories, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getInventoryCountHistories);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  arrangeProductLocation(storeId, defaults = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.arrangeProductLocation, {
          storeId,
          defaults: defaults ? JSON.stringify(defaults) : null,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.arrangeProductLocation));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateSkuLocation(location) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateSkuLocation, {
          location: JSON.stringify(location),
        }),
      )
        .then((data) => {
          resolve(data.data.updateSkuLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createItem(item, options = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createItem, {
          item: JSON.stringify(item),
          options: options ? JSON.stringify(options) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.createItem);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateItem(item, options = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateItem, {
          item: JSON.stringify(item),
          options: options ? JSON.stringify(options) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.updateItem);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteItem(item) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteItem, { item: JSON.stringify(item) }),
      )
        .then((data) => {
          resolve(data.data.deleteItem);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSalesOrder(orderId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getSalesOrder, {
          id: orderId,
        }),
      )
        .then((data) => {
          const order = data.data.getSalesOrder;
          resolve(
            order && order.payment
              ? {
                  ...order,
                  payment: JSON.parse(order.payment),
                }
              : order,
          );
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createSalesOrder(order) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.createSalesOrder, {
          input: order,
        }),
      )
        .then((data) => {
          resolve(data.data.createSalesOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreStats(storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.getStoreStats, {
          storeId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.getStoreStats));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  generatePGCode(
    amount,
    ident,
    name,
    address1,
    phone,
    email,
    itemName,
    returnUrl,
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.generatePgCode, {
          amount,
          ident,
          name,
          address1,
          phone,
          email,
          itemName,
          returnUrl,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.generatePgCode));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreProducts(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getStoreProducts, { id: storeId }),
        // authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getStoreProducts);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProducts(storeId, nextToken = null, limit = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listProductsByStore, {
          storeId,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listProductsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCategories(storeId, nextToken = null, limit = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listCategoriesByStore, {
          storeId,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listCategoriesByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProductCategoriesByCategory(
    categoryId,
    productId,
    nextToken = null,
    limit = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listProductCategoryByCategory, {
          categoryId,
          productId: productId ? { eq: productId } : {},
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listProductCategoryByCategory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProductModifiersByModifier(
    modifierId,
    productId,
    nextToken = null,
    limit = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listProductModifierByModifier, {
          modifierId,
          productId: productId ? { eq: productId } : {},
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listProductModifierByModifier);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getModifiers(storeId, nextToken = null, limit = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(custom.listModifiersByStore, {
          storeId,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listModifiersByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // ---- Equipment (비품) ----
  getEquipments(storeId, page = 1, size = 500, filters = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getEquipments, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => resolve(data.data.LS_getEquipments))
        .catch((err) => reject(err));
    });
  }

  createOrUpdateEquipment(equipment) {
    const op = equipment.id
      ? mutations.LS_updateEquipment
      : mutations.LS_createEquipment;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(op, { equipment }))
        .then((data) =>
          resolve(
            equipment.id
              ? data.data.LS_updateEquipment
              : data.data.LS_createEquipment,
          ),
        )
        .catch((err) => reject(err));
    });
  }

  deleteEquipment(equipmentId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deleteEquipment, { equipmentId }),
      )
        .then((data) => resolve(data.data.LS_deleteEquipment))
        .catch((err) => reject(err));
    });
  }

  adjustEquipments(adjustments) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_adjustEquipments, { adjustments }),
      )
        .then((data) => resolve(data.data.LS_adjustEquipments))
        .catch((err) => reject(err));
    });
  }

  applyEquipmentTransfer(history) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_applyEquipmentTransfer, { history }),
      )
        .then((data) => resolve(data.data.LS_applyEquipmentTransfer))
        .catch((err) => reject(err));
    });
  }

  // ---- 품목코드 체계 (ProductCodeRule) ----
  // amplify @model 오퍼레이션 (queries/mutations 에 generated). 인자는 input 으로 래핑.
  // listProductCodeRulesByStore: nextToken 페이지네이션 → 전체 수집 후 { items } 반환.
  async getProductCodeRules(storeId) {
    let items = [];
    let nextToken = null;
    do {
      const data = await AWSAPI.graphql(
        graphqlOperation(queries.listProductCodeRulesByStore, {
          storeId,
          limit: 200,
          nextToken,
        }),
      );
      const conn = data.data.listProductCodeRulesByStore || {};
      items = [...items, ...(conn.items || [])];
      nextToken = conn.nextToken || null;
    } while (nextToken);
    return { items };
  }

  createOrUpdateProductCodeRule(rule) {
    const op = rule.id
      ? mutations.updateProductCodeRule
      : mutations.createProductCodeRule;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(op, { input: rule }))
        .then((data) =>
          resolve(
            rule.id
              ? data.data.updateProductCodeRule
              : data.data.createProductCodeRule,
          ),
        )
        .catch((err) => reject(err));
    });
  }

  deleteProductCodeRule(ruleId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteProductCodeRule, {
          input: { id: ruleId },
        }),
      )
        .then((data) => resolve(data.data.deleteProductCodeRule))
        .catch((err) => reject(err));
    });
  }

  // 품목코드 미리보기 (채번 X — peek). input: { storeId, ruleId, selections }
  previewProductCode(input) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(mutations.LS_previewProductCode, { input }))
        .then((data) => resolve(data.data.LS_previewProductCode))
        .catch((err) => reject(err));
    });
  }

  // 품목코드 생성. input: { storeId, ruleId, skuId?, selections, commit }
  //  - commit:false = 미리보기(채번 X) / commit:true = 확정(카운터 소진). 저장 시점에 true 로 호출.
  generateProductCode(input) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(mutations.LS_generateProductCode, { input }))
        .then((data) => resolve(data.data.LS_generateProductCode))
        .catch((err) => reject(err));
    });
  }

  getEquipmentHistories(storeId, page = 1, size = 30, filters = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getEquipmentHistories, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => resolve(data.data.LS_getEquipmentHistories))
        .catch((err) => reject(err));
    });
  }

  deleteItems(itemType, itemIds, storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deleteItems, {
          itemType,
          itemIds: itemIds || [],
          storeId,
        }),
      )
        .then((data) => resolve(data.data.LS_deleteItems))
        .catch((err) => reject(err));
    });
  }

  getEmployee(employeeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(queries.getEmployee, { id: employeeId }))
        .then((data) => {
          resolve(data.data.getEmployee);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getEmployeeByEmail(email) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(custom.LS_getEmployeeByEmail, { email }))
        .then((data) => {
          resolve(data.data.LS_getEmployeeByEmail);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createOrganization(organization) {
    delete organization.employee;
    delete organization.store;
    if (!organization.locationId) {
      delete organization.locationId;
    }
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createOrganization, { input: organization }),
      )
        .then((data) => {
          resolve(data.data.createOrganization);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateOrganization(organization) {
    delete organization.employee;
    delete organization.store;
    if (!organization.locationId) {
      delete organization.locationId;
    }
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateOrganization, {
          input: organization,
        }),
      )
        .then((data) => {
          resolve(data.data.updateOrganization);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteOrganization(organizationId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteOrganization, {
          input: {
            id: organizationId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteOrganization);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // NOTE: Deprecated
  retrieveBizNumber(bizNumber) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.retrieveBizNumber, {
          bizNumber: bizNumber,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.retrieveBizNumber));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  submitCard(email, phone, storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.submitCard, {
          email,
          phone,
          storeId,
        }),
      )
        .then((data) => {
          resolve(data.data.submitCard);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStore(storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(custom.getStore, { id: storeId }))
        .then((data) => {
          resolve(data.data.getStore);
        })
        .catch((err) => {
          console.log("#####getStore", err);
          reject(err);
        });
    });
  }

  getStoreByApiKey(storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(custom.getStoreByApiKey, { id: storeId }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreContractsByBusinessOperator(businessOperatorId, nextToken, limit) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.listStoreContractByBusinessOperator, {
          businessOperatorId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listStoreContractByBusinessOperator);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationContractsByBusinessOperator(businessOperatorId, nextToken, limit) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationContractByBusinessOperator, {
          businessOperatorId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationContractByBusinessOperator);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreLoyaltyByApiKey(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(custom.getStoreLoyalty, {
          id: storeId,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationByApiKey(locationId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getLocation, {
          id: locationId,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreLocationsByApiKey(storeId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql({
        ...graphqlOperation(queries.listLocationsByStore, {
          storeId,
          nextToken,
          limit,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.listLocationsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreLocation(locationId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(queries.getLocation, { id: locationId }))
        .then((data) => {
          resolve(data.data.getLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreLocations(storeId, nextToken, limit) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationsByStore, {
          storeId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationsByStore);
        })
        .catch((err) => {
          console.log("#####getStoreLocations", err);
          reject(err);
        });
    });
  }

  getCart(cartId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getCustomerCart, {
          id: cartId,
        }),
      )
        .then((data) => {
          resolve(data.data.getCustomerCart);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  registerAccount(account) {
    const { ident, name, businessNumber, bankCode, number, holder } = account;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.registerAccount, {
          ident,
          name,
          businessNumber,
          bankCode,
          number,
          holder,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.registerAccount));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getRequestProgressEvent(eventId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getRequestProgressEvent, {
          id: eventId,
        }),
      )
        .then((data) => {
          resolve(data.data.getRequestProgressEvent);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getRequestProgressEventsByOrigin(
    originId,
    createdAt = null,
    filter = null,
    nextToken = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listRequestProgressEventByOrigin, {
          originId,
          createdAt,
          filter,
          nextToken,
          limit: 500,
        }),
      )
        .then((data) => {
          resolve(data.data.listRequestProgressEventByOrigin);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateAccount(account) {
    const { ident, name, businessNumber, bankCode, number, holder } = account;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.updateAccount, {
          ident,
          name,
          businessNumber,
          bankCode,
          number,
          holder,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.updateAccount));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCustomerGroup(group) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.createCustomerGroup, {
          input: group,
        }),
      )
        .then((data) => {
          resolve(data.data.createCustomerGroup);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateCustomerGroup(group) {
    delete group.list;
    delete group.count;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateCustomerGroup, {
          input: group,
        }),
      )
        .then((data) => {
          resolve(data.data.updateCustomerGroup);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCustomerGroup(groupId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteCustomerGroup, {
          input: {
            id: groupId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteCustomerGroup);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCustomerTag(tag) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.createCustomerTag, {
          input: tag,
        }),
      )
        .then((data) => {
          resolve(data.data.createCustomerTag);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  applyCustomerTag(tag) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_applyCustomerTag, {
          tag: JSON.stringify(tag),
        }),
      )
        .then((data) => {
          resolve(data.data.LS_applyCustomerTag);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerTags(storeId, locationId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCustomerTagsByStore, {
          storeId,
          locationId: locationId ? { eq: locationId } : null,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listCustomerTagsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerTag(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getCustomerTag, {
          id,
        }),
      )
        .then((data) => {
          resolve(data.data.getCustomerTag);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerTagsByStore(storeId, nextToken, limit, filter = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCustomerTagsByStoreId, {
          storeId,
          limit,
          nextToken,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listCustomerTagsByStoreId);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerTagsByLocation(locationId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCustomerTagsByLocationId, {
          locationId,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listCustomerTagsByLocationId);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateCustomerTag(tag) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateCustomerTag, {
          input: tag,
        }),
      )
        .then((data) => {
          resolve(data.data.updateCustomerTag);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCustomerTag(tagId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteCustomerTag, {
          input: {
            id: tagId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteCustomerTag);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCart(cart) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.createCustomerCart, {
          input: removeTypename(cart),
        }),
      )
        .then((data) => {
          resolve(data.data.createCustomerCart);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateCart(cart) {
    delete cart.store;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateCustomerCart, {
          input: removeTypename(cart),
        }),
      )
        .then((data) => {
          resolve(data.data.updateCustomerCart);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCart(cartId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteCustomerCart, {
          input: {
            id: cartId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteCustomerCart);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createOrder(order, hasDelta = false) {
    console.log("API.createOrder");
    delete order.customer;
    delete order.location;
    delete order.expdate;
    delete order.timestamp;
    delete order.orderCustomer;
    delete order.giftReceiverKey; // 서버에서만 변경하는 조회 키
    // NOTE: index key롤 사용되는 값들은 null을 넣을 수 없음
    if (!order.orderCustomerId) {
      delete order.orderCustomerId;
    }
    if (!order.customerId) {
      delete order.customerId;
    }
    if (!order.receiptCustomerId) {
      delete order.receiptCustomerId;
    }
    if (!order.receiptNumber) {
      delete order.receiptNumber;
    }
    if (!order.originId) {
      delete order.originId;
    }
    if (!order.request) {
      delete order.request;
    }
    if (!order.shippingLocationId) {
      delete order.shippingLocationId;
    }
    console.log(order);
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(graphqlOperation(custom.createOrder, { input: order }))
        .then((data) => {
          let _order = {
            ..._.cloneDeep(data.data.createOrder),
            timestamp: parseInt(new Date().getTime() / 1000, 10),
            expdate: getExpDate("OrderDelta"),
          };
          delete _order.location;
          delete _order.customer;
          delete _order.orderCustomer;
          if (!_order.shippingLocationId) {
            delete _order.shippingLocationId;
          }
          AWSAPI.graphql(
            graphqlOperation(mutations.createOrderDelta, {
              input: _order,
            }),
          )
            .then((delta) => {
              resolve(data.data.createOrder);
            })
            .catch((err) => {
              console.log(err);
              AWSAPI.graphql(
                graphqlOperation(mutations.updateOrderDelta, {
                  input: removeTypename(_order),
                }),
              )
                .then((delta) => {
                  resolve(data.data.createOrder);
                })
                .catch((_err) => {
                  reject(_err);
                });
            });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // 배송(채널 수집) 주문 필드 갱신 전용 — updateOrder 와 달리 updateOrderDelta 체인을
  // 타지 않는다. OrderDelta 는 POS 동기화 미러라 채널 주문엔 행이 없어 delta 업데이트가
  // 실패하고, 그 실패가 성공한 본 업데이트까지 reject 로 둔갑시킴(합포금지 excludeMerge
  // 미도달 무음 실패의 실경로 — 서버 20번 왕복으로 확정).
  updateOrderFlags(order) {
    return AWSAPI.graphql(
      graphqlOperation(custom.updateOrder, { input: removeTypename(order) }),
    ).then((data) => data.data.updateOrder);
  }

  updateOrder(order) {
    order = _.cloneDeep(order);
    order.updatedDeviceId = null;
    delete order.timestamp;
    delete order.expdate;
    delete order.customer;
    delete order.location;
    delete order.orderCustomer;
    delete order.giftReceiverKey; // 서버에서만 변경하는 조회 키
    if (!order.orderCustomerId) {
      delete order.orderCustomerId;
    }
    if (!order.customerId) {
      delete order.customerId;
    }
    if (!order.receiptCustomerId) {
      delete order.receiptCustomerId;
    }
    if (!order.receiptNumber) {
      delete order.receiptNumber;
    }
    if (!order.originId) {
      delete order.originId;
    }
    if (!order.request) {
      delete order.request;
    }
    if (!order.shippingLocationId) {
      delete order.shippingLocationId;
    }
    delete order.waitingTime;
    delete order.tableNumber;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(custom.updateOrder, {
          input: removeTypename(order),
        }),
      )
        .then((data) => {
          let _order = {
            ..._.cloneDeep(data.data.updateOrder),
            timestamp: parseInt(new Date().getTime() / 1000, 10),
            expdate: getExpDate("OrderDelta"),
          };
          delete _order.location;
          delete _order.customer;
          delete _order.orderCustomer;
          if (!_order.shippingLocationId) {
            delete _order.shippingLocationId;
          }
          AWSAPI.graphql(
            graphqlOperation(mutations.updateOrderDelta, {
              input: removeTypename(_order),
            }),
          )
            .then((delta) => {
              resolve(data.data.updateOrder);
            })
            .catch((err) => {
              reject(err);
            });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteOrder(orderId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteOrder, {
          input: {
            id: orderId,
          },
        }),
      )
        .then((data) => {
          if (data.data.deleteOrder) {
            let _order = {
              ..._.cloneDeep(data.data.deleteOrder),
              timestamp: parseInt(new Date().getTime() / 1000, 10),
              expdate: getExpDate("OrderDelta"),
              _delta: "DELETE",
            };
            delete _order.customer;
            delete _order.location;
            AWSAPI.graphql(
              graphqlOperation(mutations.updateOrderDelta, {
                input: removeTypename(_order),
              }),
            )
              .then((delta) => {
                resolve(data.data.deleteOrder);
              })
              .catch((err) => {
                reject(err);
              });
          } else {
            resolve({ id: orderId });
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateStore(store) {
    delete store.groups;
    delete store.location;
    delete store.locations;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateStore, {
          input: removeTypename(store),
        }),
      )
        .then((data) => {
          resolve(data.data.updateStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createLocation(location) {
    delete location.stats;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.createLocation, {
          input: removeTypename(location),
        }),
      )
        .then((data) => {
          resolve(data.data.createLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateLocation(location) {
    delete location.stats;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateLocation, {
          input: removeTypename(location),
        }),
      )
        .then((data) => {
          resolve(data.data.updateLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getNoticeCampaignResultsByLocation(
    loacationId,
    nextToken = null,
    limit = null,
  ) {
    let input = {
      recipientLocationId: loacationId,
    };
    if (limit) {
      input.limit = limit;
    }
    if (nextToken) {
      input.nextToken = nextToken;
    }

    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listNoticeCampaignResultsByLocation, input),
      )
        .then((data) => {
          resolve(data.data.listNoticeCampaignResultsByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getNoticeCampaigns(storeId, nextToken = null, limit = null) {
    let input = {
      storeId: storeId,
    };
    if (limit) {
      input.limit = limit;
    }
    if (nextToken) {
      input.nextToken = nextToken;
    }

    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listNoticeCampaignsByStore, input),
      )
        .then((data) => {
          resolve(data.data.listNoticeCampaignsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getNotices(storeId, status = null, nextToken = null, limit = null) {
    let input = {
      storeId: storeId,
    };
    if (limit) {
      input.limit = limit;
    }
    if (nextToken) {
      input.nextToken = nextToken;
    }
    if (status) {
      input.status = { eq: status };
    }

    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(graphqlOperation(queries.listNoticesByStore, input))
        .then((data) => {
          resolve(data.data.listNoticesByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getNotice(noticeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(graphqlOperation(queries.getNotice, { id: noticeId }))
        .then((data) => {
          resolve(data.data.getNotice);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createNoticeCampaign(campaign) {
    delete campaign.notice;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createNoticeCampaign, {
          input: campaign,
        }),
      )
        .then((data) => {
          resolve(data.data.createNoticeCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateNoticeCampaign(campaign) {
    delete campaign.notice;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateNoticeCampaign, {
          input: campaign,
        }),
      )
        .then((data) => {
          resolve(data.data.updateNoticeCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteNoticeCampaign(campaignId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteNoticeCampaign, {
          input: {
            id: campaignId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteNoticeCampaign);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createNoticeCampaignResult(result) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createNoticeCampaignResult, {
          input: result,
        }),
      )
        .then((data) => {
          resolve(data.data.createNoticeCampaignResult);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createNotice(notice) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createNotice, {
          input: notice,
        }),
      )
        .then((data) => {
          resolve(data.data.createNotice);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateNotice(notice) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateNotice, {
          input: notice,
        }),
      )
        .then((data) => {
          resolve(data.data.updateNotice);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteNotice(noticeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteNotice, {
          input: {
            id: noticeId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteNotice);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  generateItemTemplate(storeId, stockManageable, excelType) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.generateItemTemplate, {
          storeId,
          stockManageable,
          excelType,
        }),
      )
        .then((data) => {
          resolve(data.data.generateItemTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  parseItemTemplate(
    storeId,
    key,
    locationId,
    resource,
    excelType,
    templateSessionId = null,
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.parseItemTemplate, {
          storeId,
          key,
          locationId,
          resource,
          excelType,
          templateSessionId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.parseItemTemplate));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  syncItemTemplate(storeId, key, excelType, templateSessionId = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.syncItemTemplate, {
          storeId,
          key,
          excelType,
          templateSessionId,
        }),
      )
        .then((data) => {
          resolve(data.data.syncItemTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  generateInventoryTemplate(storeId, locationId, reason, type, options = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.generateInventoryTemplate, {
          storeId,
          locationId,
          reason,
          type,
          options: options ? JSON.stringify(options) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.generateInventoryTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  parseInventoryTemplate(
    storeId,
    employeeId,
    key,
    reason,
    type,
    options = null,
    templateSessionId = null,
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.parseInventoryTemplate, {
          storeId,
          employeeId,
          key,
          reason,
          type,
          options: options ? JSON.stringify(options) : null,
          templateSessionId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.parseInventoryTemplate));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  syncInventoryTemplate(storeId, key, reason, type, templateSessionId = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.syncInventoryTemplate, {
          storeId,
          key,
          reason,
          type,
          templateSessionId,
        }),
      )
        .then((data) => {
          resolve(data.data.syncInventoryTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  generateExcelTemplate(storeId, type) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.generateExcelTemplate, {
          storeId,
          type,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.generateExcelTemplate));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  parseExcelTemplate(storeId, type, key, returnType = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.parseExcelTemplate, {
          storeId,
          type,
          key,
          returnType,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.parseExcelTemplate));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  applyInventoryRequestHistory(history, origin, originId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.applyInventoryRequestHistory, {
          history,
          origin,
          originId,
        }),
      )
        .then((data) => {
          resolve(data.data.applyInventoryRequestHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  applyInventoryTransferHistory(
    history,
    origin,
    originId,
    templateType = null,
    templateSessionId = null,
  ) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.applyInventoryTransferHistory, {
          history,
          origin,
          originId,
          templateType,
          templateSessionId,
        }),
      )
        .then((data) => {
          resolve(data.data.applyInventoryTransferHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelInventoryTransferHistory(history, origin, originId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.cancelInventoryTransferHistory, {
          history,
          origin,
          originId,
        }),
      )
        .then((data) => {
          resolve(data.data.cancelInventoryTransferHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  applyPurchaseOrder(purchaseOrder, adjustments, origin, originId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.applyPurchaseOrder, {
          purchaseOrder,
          adjustments,
          origin,
          originId,
        }),
      )
        .then((data) => {
          resolve(data.data.applyPurchaseOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelPurchaseOrder(purchaseOrder, origin, originId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.cancelPurchaseOrder, {
          purchaseOrder,
          origin,
          originId,
        }),
      )
        .then((data) => {
          resolve(data.data.cancelPurchaseOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  applyInventoryAdjustments(adjustments, origin, originId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.applyInventoryAdjustments, {
          adjustments,
          origin,
          originId,
        }),
      )
        .then((data) => {
          resolve(data.data.applyInventoryAdjustments);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  applyInventoryCountHistory(history, origin, originId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.applyInventoryCountHistory, {
          history,
          origin,
          originId,
        }),
      )
        .then((data) => {
          resolve(data.data.applyInventoryCountHistory);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  requestCashReceipt(data) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(graphqlOperation(queries.requestCashReceipt, data))
        .then((data) => {
          resolve(JSON.parse(data.data.requestCashReceipt));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  subscribeStoreStatusUpdated(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onUpdatedStoreStatus, {
        id: storeId,
      }),
    ).subscribe({
      next: (data) => {
        callback(data.value.data.onUpdatedStoreStatus);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  subscribeRequestPrgressEvent(key, originId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onUpdatedRequestProgressEvent, {
        originId,
      }),
    ).subscribe({
      next: (data) => {
        callback(data.value.data.onUpdatedRequestProgressEvent);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  subscribeProductUpdated(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onUpdatedProduct, {
        storeId: storeId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onUpdatedProduct),
      error: (err) => console.log(err),
    });
  }

  subscribeProductDeleted(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onDeletedProduct, {
        storeId: storeId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onDeletedProduct),
      error: (err) => console.log(err),
    });
  }

  subscribeOrderUpdated(key, customerId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onOrderDeltaByCustomer, {
        // graphqlOperation(subscriptions.onOrderDelta, {
        customerId: customerId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onOrderDeltaByCustomer),
      // next: (data) => callback(data.value.data.onOrderDelta),
      error: (err) => console.log("subscribe error", err),
    });
  }

  subscribeOrderDeltaChanged(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onOrderDeltaByStore, {
        storeId: storeId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onOrderDeltaByStore),
      error: (err) => console.log("subscribe error", err),
    });
  }

  subscribeCategoryUpdated(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onUpdatedCategory, {
        storeId: storeId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onUpdatedCategory),
      error: (err) => console.log(err),
    });
  }

  subscribeCategoryDeleted(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onDeletedCategory, {
        storeId: storeId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onDeletedCategory),
      error: (err) => console.log(err),
    });
  }

  subscribeModifierUpdated(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onUpdatedModifier, {
        storeId: storeId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onUpdatedModifier),
      error: (err) => console.log(err),
    });
  }

  subscribeModifierDeleted(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onDeletedModifier, {
        storeId: storeId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onDeletedModifier),
      error: (err) => console.log(err),
    });
  }

  subscribeItemsChanged(key, storeId, callback) {
    this._subscribers[key] = AWSAPI.graphql(
      graphqlOperation(subscriptions.onUpdatedItems, {
        id: storeId,
      }),
    ).subscribe({
      next: (data) => callback(data.value.data.onUpdatedItems),
      error: (err) => console.log(err),
    });
  }

  unsubscribe(key) {
    if (_.has(this._subscribers, key)) {
      this._subscribers[key].unsubscribe();
      delete this._subscribers[key];
    }
  }

  unsubscribeAll() {
    for (let key of Object.keys(this._subscribers)) {
      this._subscribers[key].unsubscribe();
    }
    this._subscribers = {};
  }

  createTerminal(terminal) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createPaymentTerminal, {
          input: terminal,
        }),
      )
        .then((data) => {
          resolve(data.data.createPaymentTerminal);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateTerminal(terminal) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updatePaymentTerminal, {
          input: terminal,
        }),
      )
        .then((data) => {
          resolve(data.data.updatePaymentTerminal);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteTerminal(terminalId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deletePaymentTerminal, {
          input: {
            id: terminalId,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deletePaymentTerminal);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getTerminal(terminalId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.getPaymentTerminal, {
          id: terminalId,
        }),
      )
        .then((data) => {
          resolve(data.data.getPaymentTerminal);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getTerminals(nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listPaymentTerminals, { nextToken, limit }),
      )
        .then((data) => {
          resolve(data.data.listPaymentTerminals);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getTerminalsByBusinessNumber(businessNumber, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listPaymentTerminalByBusinessNumber, {
          businessNumber,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listPaymentTerminalByBusinessNumber);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getTerminalsByBusinessOperator(businessOperatorId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listPaymentTerminalByBusinessOperator, {
          businessOperatorId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listPaymentTerminalByBusinessOperator);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getTerminalsByStore(storeId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listPaymentTerminalByStore, {
          storeId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listPaymentTerminalByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getTerminalsByLocation(locationId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listPaymentTerminalByLocation, {
          locationId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listPaymentTerminalByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getRentalContractsByBusinessOperator(businessOperatorId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listRentalContractByBusinessOperator, {
          businessOperatorId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listRentalContractByBusinessOperator);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getRentalContractsByStore(storeId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listRentalContractByStore, {
          storeId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listRentalContractByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getRentalContractsByByLocation(locationId, nextToken, limit) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listRentalContractByLocation, {
          locationId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listRentalContractByLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPurchaseOrders(storeId, locationId, page, size, filters = null) {
    console.log("API.getPurchaseOrders");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getPurchaseOrders, {
          storeId,
          locationId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getPurchaseOrders);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getPurchaseOrder(purchaseOrderId) {
    console.log("API.getPurchaseOrder");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getPurchaseOrder, {
          purchaseOrderId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getPurchaseOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createPurchaseOrder(order) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_createPurchaseOrder, {
          purchaseOrder: order,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_createPurchaseOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updatePurchaseOrder(order) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_updatePurchaseOrder, {
          purchaseOrder: removeTypename(order),
        }),
      )
        .then((data) => {
          resolve(data.data.LS_updatePurchaseOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deletePurchaseOrder(orderId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deletePurchaseOrder, {
          purchaseOrderId: orderId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_deletePurchaseOrder);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  payBillings(storeId, locationId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_payBillings, {
          storeId,
          locationId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_payBillings);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getOrderCountFromReport(storeId, filters) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getOrderCount, {
          storeId,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getOrderCount);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  generateReport(storeId, item, start, end) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.generateReport, {
          storeId,
          item,
          start,
          end,
        }),
      )
        .then((data) => {
          resolve(data.data.generateReport);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  sendReportToEmail(storeId, item, filters, recepient, options = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_sendReport, {
          storeId,
          item,
          recepient,
          filters: filters ? JSON.stringify(filters) : null,
          version: "2025.3.6",
          options: options ? JSON.stringify(options) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_sendReport);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStoreContract(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getStoreContract, {
          id: id,
        }),
      )
        .then((data) => {
          resolve(data.data.getStoreContract);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createStoreContract(contract) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createStoreContract, {
          input: contract,
        }),
      )
        .then((data) => {
          resolve(data.data.createStoreContract);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateStoreContract(contract) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateStoreContract, {
          input: contract,
        }),
      )
        .then((data) => {
          resolve(data.data.updateStoreContract);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationContract(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getLocationContract, {
          id: id,
        }),
      )
        .then((data) => {
          resolve(data.data.getLocationContract);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createLocationContract(contract) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createLocationContract, {
          input: contract,
        }),
      )
        .then((data) => {
          resolve(data.data.createLocationContract);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateLocationContract(contract) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateLocationContract, {
          input: contract,
        }),
      )
        .then((data) => {
          resolve(data.data.updateLocationContract);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getBusinessOperator(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getBusinessOperator, {
          id: id,
        }),
      )
        .then((data) => {
          resolve(data.data.getBusinessOperator);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createBusinessOperator(operator) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createBusinessOperator, {
          input: operator,
        }),
      )
        .then((data) => {
          resolve(data.data.createBusinessOperator);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateBusinessOperator(operator) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateBusinessOperator, {
          input: operator,
        }),
      )
        .then((data) => {
          resolve(data.data.updateBusinessOperator);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createRentalContract(contract) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createRentalContract, {
          input: contract,
        }),
      )
        .then((data) => {
          resolve(data.data.createRentalContract);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateRentalContract(contract) {
    delete contract.businessOperator;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateRentalContract, {
          input: contract,
        }),
      )
        .then((data) => {
          resolve(data.data.updateRentalContract);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLabelTemplatesByStore(storeId) {
    console.log("API getLabelTemplatesByStore", storeId);
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listLabelTemplateByStore, {
          storeId,
        }),
      )
        .then((data) => {
          resolve(data.data.listLabelTemplateByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createLabelTemplate(template) {
    if (!template.locationId) {
      delete template.locationId;
    }
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createLabelTemplate, {
          input: template,
        }),
      )
        .then((data) => {
          resolve(data.data.createLabelTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateLabelTemplate(template) {
    delete template.horizontalCount;
    delete template.labelSetting;
    delete template.pageSetting;
    delete template.paperType;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateLabelTemplate, {
          input: template,
        }),
      )
        .then((data) => {
          resolve(data.data.updateLabelTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteLabelTemplate(id) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteLabelTemplate, {
          input: {
            id,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteLabelTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSuppliers(storeId, locationId = null) {
    console.log("API.getSuppliers");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getSuppliers, {
          storeId,
          locationId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getSuppliers);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createSupplier(supplier) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_createSupplier, { supplier }),
      )
        .then((data) => {
          resolve(data.data.LS_createSupplier);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateSupplier(supplier) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_updateSupplier, {
          supplier,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_updateSupplier);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteSupplier(supplierId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deleteSupplier, {
          supplierId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_deleteSupplier);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // ---------- Brand ----------

  listBrandsByStore(storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.listBrandsByStore, { storeId, limit: 500 }),
      )
        .then((data) => {
          resolve(data.data.listBrandsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createBrand(brand) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(mutations.createBrand, { input: brand }))
        .then((data) => {
          resolve(data.data.createBrand);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateBrand(brand) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(mutations.updateBrand, { input: brand }))
        .then((data) => {
          resolve(data.data.updateBrand);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteBrand(brandId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteBrand, { input: { id: brandId } }),
      )
        .then((data) => {
          resolve(data.data.deleteBrand);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getEnvironment(id) {
    console.log("API.getEnvironment");
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getEnvironment, {
          id,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getEnvironment);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getBarcodeCouponTransactions(couponId, recepient = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getBarcodeCouponTransactions, {
          couponId,
          recepient,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getBarcodeCouponTransactions));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCouponTransactionsByCouponStoreCustomer(
    couponTransactionsId,
    couponTransactionCustomerId = null,
    filter = null,
    limit,
    nextToken,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCouponTransactionsByCouponStoreCustomer, {
          couponTransactionsId,
          couponTransactionCustomerId: couponTransactionCustomerId
            ? { eq: couponTransactionCustomerId }
            : null,
          sortDirection: "DESC",
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listCouponTransactionsByCouponStoreCustomer);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createBarcodeCouponTransactions(transactions) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_createBarcodeCouponTransactions, {
          transactions,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.LS_createBarcodeCouponTransactions);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  registerProvider(storeId, providerId, provider) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_registerProvider, {
          storeId,
          provider,
          providerId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_registerProvider);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /**
   * 생성물(mutations.js)이 $shopNo 를 아직 선언하지 않았는지 확인한다.
   * 선언 전에는 shopNo 를 변수로 넘겨도 GraphQL 이 무시하고 **메인몰** 설정을 내려주는데,
   * 그 값을 서브몰 tiers 로 저장하면 itemId 네임스페이스(`{shopNo}:{group_name}`)가 어긋난다.
   * 그래서 호출부가 사전에 막을 수 있게 노출한다 — codegen 되면 자동으로 true 가 된다.
   */
  supportsShopScopedLoyaltyConfig() {
    return /\$shopNo/.test(mutations.LS_getLoyaltyConfig || "");
  }

  // shopNo 를 주면 해당 서브몰의 등급별 적립률(conditionTiers)을 받는다. 미지정이면 메인몰(기존 동작).
  // ⚠ 서버는 shopNo 가 빈 값이면 InvalidShopNo 로 응답한다 — 호출부가 몰 선택 상태를 보장해야 한다.
  getExternalLoyaltyConfig(storeId, provider, shopNo) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getLoyaltyConfig, {
          storeId,
          provider,
          // 스키마상 shopNo 는 String (ShopMap.shopNo 와 동일 타입)
          ...(shopNo ? { shopNo: String(shopNo) } : {}),
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getLoyaltyConfig);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderCustomers(storeId, locationId, filters) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_getProviderCustomers, {
          storeId,
          locationId,
          filters: filters ? JSON.stringify(filters) : null,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProviderCustomers).items || []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // 자사몰(cafe24/imweb) 고객 재동기화. resync=true 로 호출하면 서버가
  // 자사몰에서 최신 고객정보를 조회해 스토어 고객 데이터에 반영한다.
  // locationId 미지정 시 서버가 스토어 기본 지점(provider.locationId)을 사용.
  resyncProviderCustomer(storeId, customerId, locationId = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderCustomer, {
          storeId,
          customerId,
          locationId,
          resync: true,
        }),
      )
        .then((data) => {
          const result = data.data.LS_getProviderCustomer;
          resolve(result ? JSON.parse(result) : null);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderConfig(storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.getStoreCafe24Config, {
          id: storeId,
        }),
      )
        .then((data) => {
          resolve(data.data.getStoreCafe24Config);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateProviderConfig(config) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateStoreCafe24Config, {
          input: config,
        }),
      )
        .then((data) => {
          resolve(data.data.updateStoreCafe24Config);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderCoupons(storeId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderCoupons, {
          storeId,
          couponId: null,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProviderCoupons).items || []);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStores(nextToken, limit) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.listStores, {
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listStores);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationsByStore(storeId, nextToken, limit) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationsByStore, {
          storeId,
          nextToken,
          limit,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationsByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationDevicesByStore(
    storeId,
    timestamp = null,
    nextToken = null,
    limit = null,
    filter = null,
  ) {
    console.log("API.getLocationDevicesByStore");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationDeviceDeltaByStore, {
          storeId,
          timestamp,
          nextToken,
          limit,
          filter,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationDeviceDeltaByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getWaitingDelta(waitingId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(queries.getWaitingDelta, {
          id: waitingId,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.getWaitingDelta);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getWaitingInfo(storeId, locationId, salesDate, waitingId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_getWaitingStatus, {
          storeId: storeId,
          locationId: locationId,
          salesDate,
          waitingId,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getWaitingStatus));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelWaiting(waiting) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql({
        ...graphqlOperation(mutations.LS_updateWaiting, {
          waiting: waiting,
        }),
        authMode: AUTH_TYPE.API_KEY,
      })
        .then((data) => {
          resolve(data.data.LS_updateWaiting);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  chargeCredit(storeId, amount, quantity, locationId = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_chargeStoreCredit, {
          storeId,
          amount,
          quantity,
          locationId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_chargeStoreCredit);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCreditStatus(storeId, locationId = null) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getStoreCreditStatus, {
          storeId,
          locationId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getStoreCreditStatus));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCreditTransactionHistories(
    storeId,
    page,
    size,
    locationId = null,
    filters = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getStoreCreditTransactions, {
          storeId,
          page,
          size,
          locationId,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getStoreCreditTransactions);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  syncProviderOrders(storeId, startDate) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_syncProviderOrders, {
          storeId,
          start: startDate,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_syncProviderOrders);
        })
        .catch((err) => reject(err));
    });
  }

  getProviderProducts(storeId, size, since, search, searchType, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderProducts, {
          storeId,
          searchType,
          search,
          size,
          since,
          filters: JSON.stringify(filters),
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProviderProducts));
        })
        .catch((err) => reject(err));
    });
  }

  linkProviderProducts(
    storeId,
    options,
    pairs = [],
    excludeKeys = [],
    employeeId,
    condition,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_linkProviderProducts, {
          storeId,
          options,
          pairs,
          excludeKeys,
          employeeId,
          condition,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_linkProviderProducts);
        })
        .catch((err) => reject(err));
    });
  }

  unlinkProviderProducts(storeId, skuIds) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_unlinkProviderProducts, {
          storeId,
          skuIds,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_unlinkProviderProducts);
        })
        .catch((err) => reject(err));
    });
  }

  syncProviderInventories(storeId, employeeId, skuIds, condition, version) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_syncProviderInventories, {
          storeId,
          employeeId,
          skuIds,
          condition,
          version,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_syncProviderInventories);
        })
        .catch((err) => reject(err));
    });
  }

  getInventorySyncHistories(storeId, page, size, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getInventorySyncHistories, {
          storeId,
          page,
          size,
          filters: JSON.stringify(filters),
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getInventorySyncHistories);
        })
        .catch((err) => reject(err));
    });
  }

  getProviderProductsLinkHistories(
    storeId,
    createdAt,
    filter = null,
    limit,
    nextToken,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listItemLinkHistoryByStore, {
          storeId,
          createdAt,
          sortDirection: "DESC",
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listItemLinkHistoryByStore);
        })
        .catch((err) => reject(err));
    });
  }

  syncProviderProducts(storeId, skuIds, excludeKeys, employeeId, condition) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_syncProviderProducts, {
          storeId,
          skuIds,
          excludeKeys,
          employeeId,
          condition,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_syncProviderProducts);
        })
        .catch((err) => reject(err));
    });
  }

  getOnoffConfig(storeId, stage) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getOnoffConfig, {
          storeId,
          stage,
        }),
      )
        .then((data) => {
          resolve(data.data.getOnoffConfig);
        })
        .catch((err) => reject(err));
    });
  }

  getCafe24Auth(ident) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getCafe24Auth, {
          id: ident,
        }),
      )
        .then((data) => {
          resolve(data.data.getCafe24Auth);
        })
        .catch((err) => reject(err));
    });
  }

  createOnoffConfig(config) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createOnoffConfig, {
          input: config,
        }),
      )
        .then((data) => {
          resolve(data.data.createOnoffConfig);
        })
        .catch((err) => reject(err));
    });
  }

  updateOnoffConfig(config) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateOnoffConfig, {
          input: config,
        }),
      )
        .then((data) => {
          resolve(data.data.updateOnoffConfig);
        })
        .catch((err) => reject(err));
    });
  }

  deleteOnoffConfig(storeId, stage) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteOnoffConfig, {
          input: {
            storeId,
            stage,
          },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteOnoffConfig);
        })
        .catch((err) => reject(err));
    });
  }

  getCherryPickers(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCustomerCherrypickers, {
          storeId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerCherrypickers));
        })
        .catch((err) => reject(err));
    });
  }

  getBlackList(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCustomerBlacklists, {
          storeId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerBlacklists));
        })
        .catch((err) => reject(err));
    });
  }

  getCustomerTierHistories(storeId, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCustomerTierHistories, {
          storeId,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerTierHistories));
        })
        .catch((err) => reject(err));
    });
  }

  updateCustomerTier(storeId, customerId, tierId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_updateCustomerTier, {
          storeId,
          customerId,
          tierId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_updateCustomerTier);
        })
        .catch((err) => reject(err));
    });
  }

  updateCustomerBlackList(storeId, customerId, active) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_updateCustomerBlacklist, {
          storeId,
          customerId,
          active,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_updateCustomerBlacklist);
        })
        .catch((err) => reject(err));
    });
  }

  sendNotice(recipient, category, params, storeId, locationId) {
    console.log("API.sendNotice");
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.sendNotice, {
          recipient: JSON.stringify(recipient),
          category,
          params: JSON.stringify(params),
          storeId,
          locationId,
        }),
      )
        .then((data) => {
          resolve(data.data.sendNotice);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProductInventories(
    storeId,
    page,
    size,
    filters = null,
    locationId = null,
  ) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getProductInventories, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
          locationId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProductInventories));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSkuInventoryReport(storeId, skuId, start, end, locationId = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getSkuInventoryReport, {
          storeId,
          skuId,
          start,
          end,
          locationId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getSkuInventoryReport));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getSkuSalesReport(storeId, skuIds) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getSkuSalesReport, {
          storeId,
          skuIds,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getSkuSalesReport));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProductList(storeId, page, size, filters = null, locationId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getProductList, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
          locationId,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getProductList));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProductsByIds(storeId, productIds) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getProductsByIds, {
          storeId,
          productIds,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getProductsByIds);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /**
   * 품목코드(model) / 바코드(unit|barcode) 사전 중복 조회.
   *
   * 서버 계약 (sales-server src/item.py `_checkProductCodes`) — 두 API 응답 형태 동일:
   *   {
   *     "duplicated": ["8801234567890"],          // 요청값 중 중복인 것만, 요청 순서 유지
   *     "items": [{ "barcode"|"model": "8801234567890",  // 요청값을 그대로 echo
   *                 "prodName": "...", "skuName": "...",
   *                 "productId": "prod_x", "skuId": "sku_1" }]
   *   }
   * - 완전 일치(exact) 판정. 같은 storeId 안에서만. 삭제(soft delete)된 제품은 제외.
   * - excludeProductId 가 오면 그 제품의 SKU 는 전부 제외 (수정 시 자기 자신 회피).
   * - 빈 값/공백은 서버가 알아서 제외한다.
   * - 바코드는 ProductSku.unit / barcode 두 컬럼 중 하나라도 겹치면 중복이다
   *   (레거시 POS·오픈API 는 unit 에만 쓴다).
   * - 한 값이 여러 SKU 에서 쓰이면 items 엔트리도 여러 개다.
   * 반환 타입이 AWSJSON 이라 문자열로 내려오면 JSON.parse 가 필요하다.
   */
  _checkProductCodes(query, storeId, field, values, excludeProductId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(query, {
          storeId,
          [field === "barcode" ? "barcodes" : "models"]: values,
          excludeProductId,
        }),
      )
        .then((data) => {
          const raw =
            data.data[
              field === "barcode"
                ? "LS_checkProductBarcodes"
                : "LS_checkProductModels"
            ];
          let parsed = raw;
          if (typeof raw === "string") {
            try {
              parsed = JSON.parse(raw);
            } catch (e) {
              parsed = null;
            }
          }
          resolve({
            duplicated: (parsed?.duplicated || []).map(String),
            items: parsed?.items || [],
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  checkProductBarcodes(storeId, barcodes, excludeProductId = null) {
    return this._checkProductCodes(
      queries.LS_checkProductBarcodes,
      storeId,
      "barcode",
      barcodes,
      excludeProductId,
    );
  }

  checkProductModels(storeId, models, excludeProductId = null) {
    return this._checkProductCodes(
      queries.LS_checkProductModels,
      storeId,
      "model",
      models,
      excludeProductId,
    );
  }

  getAllStores(limit, nextToken, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listStores, {
          filter: filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listStores);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getAllStoreContract(limit, nextToken, filter) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listStoreContracts, {
          filter: filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listStoreContracts);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  subscribePaidPlan(storeId, planId, billingCycle) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_subscribeContractSubscription, {
          storeId,
          planId,
          billingCycle,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_subscribeContractSubscription);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  cancelPaidPlan(storeId, planId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_cancelContractSubscription, {
          storeId,
          planId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_cancelContractSubscription);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  activeLocation(storeId, locationId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_activeLocation, {
          storeId,
          locationId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_activeLocation);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getContractSubscriptionPlans() {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listContractSubscriptionPlans, {
          filter: null,
          limit: 100,
        }),
      )
        .then((data) => {
          resolve(data.data.listContractSubscriptionPlans);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getContractSubcriptionPlan(planId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getContractSubscriptionPlan, {
          planId,
        }),
      )
        .then((data) => {
          resolve(data.data.getContractSubscriptionPlan);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationStats(locationId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.getLocationStats, {
          id: locationId,
        }),
      )
        .then((data) => {
          resolve(data.data.getLocationStats);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCustomerEvent(customerEvent) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createCustomerEvent, {
          input: customerEvent,
        }),
      )
        .then((data) => {
          resolve(data.data.createCustomerEvent);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateCustomerEvent(customerEvent) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateCustomerEvent, {
          input: customerEvent,
        }),
      )
        .then((data) => {
          resolve(data.data.updateCustomerEvent);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCustomerEvent(eventId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteCustomerEvent, {
          input: { id: eventId },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteCustomerEvent);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerEventsByStore(storeId, limit, nextToken) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCustomerEventByStore, {
          storeId: storeId,
          createdAt: null,
          sortDirection: "DESC",
          filter: null,
          limit: limit,
          nextToken: nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listCustomerEventByStore);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerEventReport(eventId, start, end) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCustomerEventReport, {
          eventId: eventId,
          start,
          end,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerEventReport));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerEventList(eventId, item, page, size) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getCustomerEventList, {
          eventId,
          item,
          page,
          size,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getCustomerEventList));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderCustomerCount(storeId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderCustomerCount, {
          storeId,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getProviderCustomerCount);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  addProviderScript(storeId, provider, scriptType) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_addProviderScript, {
          storeId,
          provider,
          scriptType,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_addProviderScript);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getProviderScript(storeId, provider, scriptType) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_getProviderScript, {
          storeId,
          provider,
          scriptType,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_getProviderScript);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteProviderScript(storeId, provider, scriptType) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.LS_deleteProviderScript, {
          storeId,
          provider,
          scriptType,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_deleteProviderScript);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getLocationDeviceDeltas(filter, limit, nextToken) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listLocationDeviceDeltas, {
          filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listLocationDeviceDeltas);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createCampaignTemplate(template) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.createCampaignTemplate, {
          input: template,
        }),
      )
        .then((data) => {
          resolve(data.data.createCampaignTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateCampaignTemplate(template) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.updateCampaignTemplate, {
          input: template,
        }),
      )
        .then((data) => {
          resolve(data.data.updateCampaignTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  deleteCampaignTemplate(templateId) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteCampaignTemplate, {
          input: { id: templateId },
        }),
      )
        .then((data) => {
          resolve(data.data.deleteCampaignTemplate);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCampaignTemplates(filter, limit, nextToken) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.listCampaignTemplates, {
          filter: filter,
          limit,
          nextToken,
        }),
      )
        .then((data) => {
          resolve(data.data.listCampaignTemplates);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getCustomerTagTrend(tagId, start, end) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_countCustomersByTag, {
          tagId,
          start,
          end,
        }),
      )
        .then((data) => {
          resolve(data.data.LS_countCustomersByTag);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getMessageCampaigns(storeId, page, size, filters) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getMessageCampaigns, {
          storeId,
          page,
          size,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          // 브랜드 AWSJSON(listItems/cards/tail)은 문자열로 온다 — 리스트도 파싱해
          // 넘겨야 활성화 게이트·프리뷰 모달이 객체를 그대로 쓸 수 있다.
          resolve(parseBrandCampaignList(data.data.LS_getMessageCampaigns));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getMessageCampaignReport(storeId, start, end, filters = null) {
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(
        graphqlOperation(queries.LS_getMessageCampaignReport, {
          storeId,
          start,
          end,
          filters: filters ? JSON.stringify(filters) : null,
        }),
      )
        .then((data) => {
          resolve(JSON.parse(data.data.LS_getMessageCampaignReport));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // ===== 배송주문 관리 — 발주(주문 가져오기/발주확인/매칭/합포) =====
  // sales-server FIELD_MAP(erp-scheme) 계약. 반환은 AWSJSON(문자열) 가정 — 방어적 JSON.parse.

  _parseDeliveryResult(raw) {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  }

  _callDeliveryField(doc, fieldName, variables) {
    const parse = this._parseDeliveryResult;
    return new Promise(function (resolve, reject) {
      AWSAPI.graphql(graphqlOperation(doc, variables))
        .then((data) => {
          resolve(parse(data.data[fieldName]));
        })
        .catch((err) => reject(err));
    });
  }

  importChannelOrders(storeId, origins = null, start = null, end = null) {
    return this._callDeliveryField(mutations.LS_importChannelOrders, "LS_importChannelOrders", {
      storeId,
      origins,
      start,
      end,
    });
  }

  listPreConfirmOrders(storeId, { search, origins } = {}) {
    return this._callDeliveryField(queries.LS_listPreConfirmOrders, "LS_listPreConfirmOrders", {
      storeId,
      search,
      origins,
    });
  }

  reloadDeliveryOrders(
    storeId,
    channelOrderIds = null,
    retailStates = null,
    origins = null,
  ) {
    return this._callDeliveryField(mutations.LS_reloadDeliveryOrders, "LS_reloadDeliveryOrders", {
      storeId,
      channelOrderIds,
      retailStates,
      origins,
    });
  }

  deleteUnconfirmedOrders(storeId, channelOrderIds = null) {
    return this._callDeliveryField(
      mutations.LS_deleteUnconfirmedOrders,
      "LS_deleteUnconfirmedOrders",
      { storeId, channelOrderIds },
    );
  }

  confirmDeliveryOrders(storeId, orderIds, employeeId, locationId, eventId) {
    return this._callDeliveryField(
      mutations.LS_confirmDeliveryOrders,
      "LS_confirmDeliveryOrders",
      { storeId, orderIds, employeeId, locationId, eventId },
    );
  }

  listDeliveryStageOrders(storeId) {
    return this._callDeliveryField(
      queries.LS_listDeliveryStageOrders,
      "LS_listDeliveryStageOrders",
      { storeId },
    );
  }

  autoMatchDeliveryOrders(storeId, orderIds, eventId) {
    return this._callDeliveryField(
      mutations.LS_autoMatchDeliveryOrders,
      "LS_autoMatchDeliveryOrders",
      { storeId, orderIds, eventId },
    );
  }

  mergeCheckDeliveryOrders(storeId, orderIds = null) {
    return this._callDeliveryField(
      mutations.LS_mergeCheckDeliveryOrders,
      "LS_mergeCheckDeliveryOrders",
      { storeId, orderIds },
    );
  }

  saveManualMatch(storeId, orderId, orderItemId, skus, saveLink = true) {
    return this._callDeliveryField(mutations.LS_saveManualMatch, "LS_saveManualMatch", {
      storeId,
      orderId,
      orderItemId,
      skus: JSON.stringify(skus),
      saveLink,
    });
  }

  // 확장 필터(origin~size) 포함 배포 완료(2026-07-20 codegen 반영 확인)
  listItemLinks(
    storeId,
    {
      skuId,
      originId,
      productName,
      limit,
      origin,
      originProductCode,
      skuName,
      skuCode,
      isSet,
      start,
      end,
      page,
      size,
    } = {},
  ) {
    return this._callDeliveryField(queries.LS_listItemLinks, "LS_listItemLinks", {
      storeId,
      skuId,
      originId,
      productName,
      limit,
      origin,
      originProductCode,
      skuName,
      skuCode,
      isSet,
      start,
      end,
      page,
      size,
    });
  }

  // 매칭 사전 등록/수정 — {saved, links, duplicates, failed, warnings}
  registerItemLinks(storeId, { groups, overwrite, employeeId } = {}) {
    return this._callDeliveryField(mutations.LS_registerItemLinks, "LS_registerItemLinks", {
      storeId,
      groups: groups ? JSON.stringify(groups) : null,
      overwrite,
      employeeId,
    });
  }

  // 매칭 조회 엑셀 다운로드 — {key, url, count}
  exportItemLinksExcel(storeId, filters = {}) {
    return this._callDeliveryField(
      queries.LS_exportItemLinksExcel,
      "LS_exportItemLinksExcel",
      { storeId, ...filters },
    );
  }

  // 미사용(미집화) 송장 조회 — {count, rows}
  listUnusedTrackings(storeId, filters = {}) {
    return this._callDeliveryField(queries.LS_listUnusedTrackings, "LS_listUnusedTrackings", {
      storeId,
      ...filters,
    });
  }

  // 미사용 송장 일괄 삭제 — targets=[{orderId, receiverId}]. {deleted, failed, results}
  deleteUnusedTrackings(storeId, { targets, employeeId } = {}) {
    return this._callDeliveryField(
      mutations.LS_deleteUnusedTrackings,
      "LS_deleteUnusedTrackings",
      { storeId, targets: targets ? JSON.stringify(targets) : null, employeeId },
    );
  }

  // 폐기(미사용) 송장 이력 조회 — {total, page, size, rows}. 미사용 송장 =
  // 교체발번·송장삭제로 폐기된 번호(CanceledWaybill, 우진 확정 2026-07-17)
  listCanceledWaybills(storeId, { start, end, page, size } = {}) {
    return this._callDeliveryField(queries.LS_listCanceledWaybills, "LS_listCanceledWaybills", {
      storeId,
      start,
      end,
      page,
      size,
    });
  }

  // 미사용 송장 엑셀 다운로드(CanceledWaybill 기반 8컬럼) — {key, url, count}
  exportUnusedTrackingsExcel(storeId, { start, end, trackingNumber } = {}) {
    return this._callDeliveryField(
      queries.LS_exportUnusedTrackingsExcel,
      "LS_exportUnusedTrackingsExcel",
      { storeId, start, end, trackingNumber },
    );
  }

  deleteItemLink(storeId, { originId, skuId, deleteAll } = {}) {
    return this._callDeliveryField(mutations.LS_deleteItemLink, "LS_deleteItemLink", {
      storeId,
      originId,
      skuId,
      deleteAll,
    });
  }

  listConfirmBatches(storeId, { limit = 50, start, end, origins, employeeId } = {}) {
    return this._callDeliveryField(queries.LS_listConfirmBatches, "LS_listConfirmBatches", {
      storeId,
      limit,
      start,
      end,
      origins,
      employeeId,
    });
  }

  cancelConfirmOrders(storeId, batchId = null, orderIds = null) {
    return this._callDeliveryField(mutations.LS_cancelConfirmOrders, "LS_cancelConfirmOrders", {
      storeId,
      batchId,
      orderIds,
    });
  }

  confirmMergeOrders(storeId, { mergeGroupId, orderIds, employeeId, locationId } = {}) {
    return this._callDeliveryField(mutations.LS_confirmMergeOrders, "LS_confirmMergeOrders", {
      storeId,
      mergeGroupId,
      orderIds,
      employeeId,
      locationId,
    });
  }

  excludeMergeOrders(storeId, { orderIds, mergeGroupId, employeeId, locationId } = {}) {
    return this._callDeliveryField(mutations.LS_excludeMergeOrders, "LS_excludeMergeOrders", {
      storeId,
      orderIds,
      mergeGroupId,
      employeeId,
      locationId,
    });
  }

  manualMergeOrders(storeId, orderIds, primaryOrderId, employeeId) {
    // employeeId — 작업자 감사 기록(스키마 push 2026-07-24, CSActivity merge.manual)
    return this._callDeliveryField(mutations.LS_manualMergeOrders, "LS_manualMergeOrders", {
      storeId,
      orderIds,
      primaryOrderId,
      employeeId: employeeId || null,
    });
  }

  createManualOrder(storeId, order) {
    return this._callDeliveryField(mutations.LS_createManualOrder, "LS_createManualOrder", {
      storeId,
      order: JSON.stringify(order),
    });
  }

  uploadManualOrderRows(storeId, rows) {
    return this._callDeliveryField(
      mutations.LS_uploadManualOrderRows,
      "LS_uploadManualOrderRows",
      { storeId, rows: JSON.stringify(rows) },
    );
  }

  // ===== 배송주문 관리 — 송장(발번/출력차수/발송처리/가송장/배송관리) =====
  // 2026-07-07 매핑 검증 완료(LS_ 접두·인자 일치). todo: 응답 shape 실데이터 e2e 확인.

  listPrintTargets(storeId, { start, end } = {}) {
    return this._callDeliveryField(queries.LS_listPrintTargets, "LS_listPrintTargets", {
      storeId,
      start: start || undefined,
      end: end || undefined,
    });
  }

  listDeliveryStageOrdersByTab(storeId, tab) {
    return this._callDeliveryField(
      queries.LS_listDeliveryStageOrdersByTab,
      "LS_listDeliveryStageOrdersByTab",
      { storeId, tab },
    );
  }

  listPrintBatches(storeId, limit = 50) {
    return this._callDeliveryField(queries.LS_listPrintBatches, "LS_listPrintBatches", {
      storeId,
      limit,
    });
  }

  listProvisionOrders(storeId) {
    return this._callDeliveryField(queries.LS_listProvisionOrders, "LS_listProvisionOrders", {
      storeId,
    });
  }

  // AWSJSON 스칼라 반환 = 셀렉션 셋이 없어 custom 문서가 불필요 — codegen 산출물(mutations) 직접 사용
  exportPrintTargetsExcel(storeId) {
    return this._callDeliveryField(
      mutations.LS_exportPrintTargetsExcel,
      "LS_exportPrintTargetsExcel",
      { storeId },
    );
  }

  // [테스트 출력] 미저장 양식옵션(template) → 샘플 라벨 PDF presigned URL. 부수효과 0.
  testPrintWaybill(storeId, template) {
    return this._callDeliveryField(
      mutations.LS_testPrintWaybill,
      "LS_testPrintWaybill",
      { storeId, template: JSON.stringify(template || {}) },
    );
  }

  trackShipment(storeId, trackingNumber, carrierCode) {
    return this._callDeliveryField(queries.LS_trackShipment, "LS_trackShipment", {
      storeId,
      trackingNumber,
      carrierCode,
    });
  }

  checkDeliveryDelays(storeId) {
    return this._callDeliveryField(queries.LS_checkDeliveryDelays, "LS_checkDeliveryDelays", {
      storeId,
    });
  }

  // 합포 설정 현황 조회 — {count, configuredCount, page, size, rows}
  listSkuMergeRules(storeId, { field, keyword, status, page, size } = {}) {
    return this._callDeliveryField(queries.LS_listSkuMergeRules, "LS_listSkuMergeRules", {
      storeId,
      field,
      keyword,
      status,
      page,
      size,
    });
  }

  // 합포 설정 업데이트(단건·일괄·전체해제) — {updated, cleared, failed}
  updateSkuMergeRules(storeId, { updates, clearAll, employeeId } = {}) {
    return this._callDeliveryField(mutations.LS_updateSkuMergeRules, "LS_updateSkuMergeRules", {
      storeId,
      updates: updates ? JSON.stringify(updates) : null,
      clearAll,
      employeeId,
    });
  }

  // 택배사 계약상태 확인 — 모달 입력 credentials 우선. {ok, code, expiresAt?, error?}
  checkCarrierContract(storeId, { code, credentials } = {}) {
    return this._callDeliveryField(mutations.LS_checkCarrierContract, "LS_checkCarrierContract", {
      storeId,
      code,
      credentials: credentials ? JSON.stringify(credentials) : null,
    });
  }

  // 택배사 테스트 발번(실채번 1건) — {ok, code, trackingNumber?, error?}
  testCarrierWaybill(storeId, { code, credentials } = {}) {
    return this._callDeliveryField(mutations.LS_testCarrierWaybill, "LS_testCarrierWaybill", {
      storeId,
      code,
      credentials: credentials ? JSON.stringify(credentials) : null,
    });
  }

  // ----- 배송 환경설정 — ShippingConfig / Carrier 모델 CRUD (codegen 문서 사용) -----

  // 스토어당 1건 전제 — 중복 행 존재 시 서버 리더(_resolvePickupLocationId 등,
  // scan_index_forward=False=최신 행)와 같은 행을 보도록 DESC 로 통일
  async getShippingConfig(storeId) {
    const data = await AWSAPI.graphql(
      graphqlOperation(queries.listShippingConfigByStore, {
        storeId,
        limit: 1,
        sortDirection: "DESC",
      }),
    );
    const conn = data.data.listShippingConfigByStore || {};
    return (conn.items || [])[0] || null;
  }

  createShippingConfig(input) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(mutations.createShippingConfig, { input }))
        .then((data) => resolve(data.data.createShippingConfig))
        .catch((err) => reject(err));
    });
  }

  updateShippingConfig(input) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(mutations.updateShippingConfig, { input }))
        .then((data) => resolve(data.data.updateShippingConfig))
        .catch((err) => reject(err));
    });
  }

  async getShippingCarriers(storeId) {
    let items = [];
    let nextToken = null;
    do {
      const data = await AWSAPI.graphql(
        graphqlOperation(queries.listCarrierByStore, {
          storeId,
          limit: 200,
          nextToken,
        }),
      );
      const conn = data.data.listCarrierByStore || {};
      items = [...items, ...(conn.items || [])];
      nextToken = conn.nextToken || null;
    } while (nextToken);
    return { items };
  }

  // isNew: 클라이언트 발급 id 로 생성하는 경우가 있어 id 유무로 create/update 판별 불가
  createOrUpdateCarrier(carrier, isNew = !carrier.id) {
    const op = isNew ? mutations.createCarrier : mutations.updateCarrier;
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(graphqlOperation(op, { input: carrier }))
        .then((data) =>
          resolve(isNew ? data.data.createCarrier : data.data.updateCarrier),
        )
        .catch((err) => reject(err));
    });
  }

  deleteCarrier(carrierId) {
    return new Promise((resolve, reject) => {
      AWSAPI.graphql(
        graphqlOperation(mutations.deleteCarrier, { input: { id: carrierId } }),
      )
        .then((data) => resolve(data.data.deleteCarrier))
        .catch((err) => reject(err));
    });
  }

  // ----- 양식(ShipmentLabelTemplate) 전용 뮤테이션 — 서버 배포 7/23 -----
  // Carrier.templates 통짜 교체(createOrUpdateCarrier) 대체. 응답 = 해당 캐리어
  // templates 전체(AWSJSON) 동봉 계약 — isDefault 전역 1개 불변식은 서버가 처리.

  // template = ShipmentLabelTemplateInput 형태 JSON(template.id 기준 서버 upsert)
  saveShipmentTemplate(storeId, carrierId, template, employeeId) {
    return this._callDeliveryField(
      mutations.LS_saveShipmentTemplate,
      "LS_saveShipmentTemplate",
      { storeId, carrierId, template: JSON.stringify(template), employeeId },
    );
  }

  copyShipmentTemplate(storeId, carrierId, templateId, name, employeeId) {
    return this._callDeliveryField(
      mutations.LS_copyShipmentTemplate,
      "LS_copyShipmentTemplate",
      { storeId, carrierId, templateId, name, employeeId },
    );
  }

  deleteShipmentTemplate(storeId, carrierId, templateId, employeeId) {
    return this._callDeliveryField(
      mutations.LS_deleteShipmentTemplate,
      "LS_deleteShipmentTemplate",
      { storeId, carrierId, templateId, employeeId },
    );
  }

  issueDeliveryTracking(storeId, { orderIds, carrierCode, locationId, employeeId } = {}) {
    return this._callDeliveryField(
      mutations.LS_issueDeliveryTracking,
      "LS_issueDeliveryTracking",
      { storeId, orderIds, carrierCode, locationId, employeeId },
    );
  }

  deleteDeliveryTracking(storeId, orderId, employeeId, receiverId) {
    // receiverId — 배송건 단위(옵션). 스키마 인자 배포 전엔 문서 미선언이라 무시(무중단).
    return this._callDeliveryField(
      mutations.LS_deleteDeliveryTracking,
      "LS_deleteDeliveryTracking",
      { storeId, orderId, receiverId, employeeId },
    );
  }

  pushDeliveryShipment(storeId, { orderIds, status, receiverId, employeeId } = {}) {
    // receiverId — 배송건 단위(옵션). 스키마 인자 배포 전엔 문서 미선언이라 무시(무중단).
    return this._callDeliveryField(
      mutations.LS_pushDeliveryShipment,
      "LS_pushDeliveryShipment",
      { storeId, orderIds, status, receiverId, employeeId },
    );
  }

  applyExternalTracking(storeId, rows, employeeId) {
    return this._callDeliveryField(
      mutations.LS_applyExternalTracking,
      "LS_applyExternalTracking",
      { storeId, rows: JSON.stringify(rows), employeeId },
    );
  }

  issueProvisionTracking(storeId, orderIds, locationId, employeeId) {
    return this._callDeliveryField(
      mutations.LS_issueProvisionTracking,
      "LS_issueProvisionTracking",
      { storeId, orderIds, locationId, employeeId },
    );
  }

  convertProvisionToReal(storeId, { orderIds, carrierCode, locationId, employeeId } = {}) {
    return this._callDeliveryField(
      mutations.LS_convertProvisionToReal,
      "LS_convertProvisionToReal",
      { storeId, orderIds, carrierCode, locationId, employeeId },
    );
  }

  completeProvisionDelivery(storeId, orderIds, employeeId, receiverId) {
    // receiverId — 가송장 배송건 단위(옵션). 스키마 인자 배포 전엔 무시(무중단).
    return this._callDeliveryField(
      mutations.LS_completeProvisionDelivery,
      "LS_completeProvisionDelivery",
      { storeId, orderIds, receiverId, employeeId },
    );
  }

  splitDeliveryShipment(storeId, orderId, itemIds, employeeId) {
    return this._callDeliveryField(
      mutations.LS_splitDeliveryShipment,
      "LS_splitDeliveryShipment",
      { storeId, orderId, itemIds, employeeId },
    );
  }

  addShipmentBox(storeId, { orderId, itemIds, issue, employeeId } = {}) {
    return this._callDeliveryField(mutations.LS_addShipmentBox, "LS_addShipmentBox", {
      storeId,
      orderId,
      itemIds,
      issue,
      employeeId,
    });
  }

  // 추가송장(다박스) — 신 API. 원송장 유지·박스별 새 receiver+발번(재고 미차감).
  // 구 addShipmentBox(기존 송장 취소 후 재발급) 대체. count 상한 5(서버).
  // contents = 라벨 인쇄용 [{name, quantity}] (AWSJSON). boxType/freightType 서버 지원.
  addShipmentReceiver(
    storeId,
    { orderId, receiverId, count, boxType, freightType, contents, employeeId } = {},
  ) {
    return this._callDeliveryField(
      mutations.LS_addShipmentReceiver,
      "LS_addShipmentReceiver",
      {
        storeId,
        orderId,
        receiverId,
        count,
        boxType,
        freightType,
        contents: this._json(contents),
        employeeId,
      },
    );
  }

  reissueDeliveryTracking(
    storeId,
    { orderId, carrierCode, locationId, receiverId, employeeId } = {},
  ) {
    // receiverId — 배송건 단위(옵션). 스키마 인자 배포 전엔 무시(무중단).
    return this._callDeliveryField(
      mutations.LS_reissueDeliveryTracking,
      "LS_reissueDeliveryTracking",
      { storeId, orderId, carrierCode, locationId, receiverId, employeeId },
    );
  }

  changeShippingAfterIssue(storeId, { orderId, name, phone, zip, address1, address2, carrierCode, employeeId } = {}) {
    return this._callDeliveryField(
      mutations.LS_changeShippingAfterIssue,
      "LS_changeShippingAfterIssue",
      { storeId, orderId, name, phone, zip, address1, address2, carrierCode, employeeId },
    );
  }

  createPrintBatch(storeId, { orderIds, shippingCompanyCode, employeeId, templateId } = {}) {
    // templateId — 송장 양식 선택(스키마 push 2026-07-24). 미지정 = 서버 기본(isDefault) 폴백
    return this._callDeliveryField(mutations.LS_createPrintBatch, "LS_createPrintBatch", {
      storeId,
      orderIds,
      shippingCompanyCode,
      employeeId,
      templateId: templateId || null,
    });
  }

  reprintBatch(storeId, batchId, employeeId) {
    return this._callDeliveryField(mutations.LS_reprintBatch, "LS_reprintBatch", {
      storeId,
      batchId,
      employeeId,
    });
  }

  // 작업지시서 데이터 조회 — 차수 단위 블록 조립 JSON(엑셀 생성은 front, 상태 기록 없음
  // — 반복 출력 가능). {batch, excluded:{canceled, activeCs, unissued, details}, blocks}
  getWorkOrderSheet(storeId, batchId, employeeId) {
    return this._callDeliveryField(queries.LS_getWorkOrderSheet, "LS_getWorkOrderSheet", {
      storeId,
      batchId,
      employeeId,
    });
  }

  // ===== 배송주문 관리 — CS(클레임/주문 편집·운영/결제·정산/수집·승인) =====
  // 게이팅 에러({error, needsRefundBank|needsDeleteTracking|feePending|duplicatedItemIds})는
  // 정상 resolve 페이로드 — throw 아님. 워커가 결과를 그대로 리듀서에 넣고 패널이 분기.
  // AWSJSON 인자(items/refundBank 등)는 제공 시에만 JSON.stringify.
  // listCsTasks 의 status/types 는 배포 타입 String/[String] — 그대로 전달(직렬화 없음).

  // undefined·null 키 제거 — "미지정"은 키 자체를 빼야 서버 기본값이 산다.
  // null 을 명시로 보내면 파이썬 기본값(reasonCode="I", pickupMethod="customer" 등)이
  // 덮어써진다(dict.get(k, default) 는 키가 있고 값이 None 이면 None 을 돌려준다).
  _stripUndefined(vars) {
    return Object.keys(vars).reduce((acc, key) => {
      if (vars[key] !== undefined && vars[key] !== null) acc[key] = vars[key];
      return acc;
    }, {});
  }

  // ----- 조회 4종 — 2026-07-10 스키마 배포 확인(시그니처 대조 완료) -----

  listCsTasks(storeId, { status, types, start, end, assigneeId, orderId, page, size } = {}) {
    return this._callDeliveryField(queries.LS_listCsTasks, "LS_listCsTasks", {
      storeId,
      status: status || null,
      types: types || null,
      start,
      end,
      assigneeId,
      orderId,
      page,
      size,
    });
  }

  // CS 목록 주문 단위 조회(B안) — 서버 배포 7/23: queries.js codegen 생성 확인,
  // custom.js 브리지 제거(인자 동일 — storeId/retailStates/csTypes/csStatus/provision/
  // start/end/page/size). 응답(AWSJSON) = {total, page, size, rows:[{orderId, origin,
  //   originId, number, numberPrefix, retailState, state, mergeGroupId, mergeBlocked,
  //   receiverName, productName, optionName, itemCount, quantity, trackingNumbers,
  //   csTasks:[{id,type,status,reasonCode,createdAt}], lastMemo}]}
  listCsOrders(
    storeId,
    { retailStates, csTypes, csStatus, provision, start, end, page, size } = {},
  ) {
    return this._callDeliveryField(queries.LS_listCsOrders, "LS_listCsOrders", {
      storeId,
      retailStates: retailStates && retailStates.length ? retailStates : null,
      csTypes: csTypes && csTypes.length ? csTypes : null,
      csStatus: csStatus || null,
      // provision=true → 가송장 보유 주문만(서버 배포 7/23 — 가송장 필터 단일화)
      provision: provision || null,
      start,
      end,
      page,
      size,
    });
  }

  countCsTasks(storeId) {
    return this._callDeliveryField(queries.LS_countCsTasks, "LS_countCsTasks", {
      storeId,
    });
  }

  listCsActivities(storeId, { orderId, taskId, start, end, page, size } = {}) {
    return this._callDeliveryField(queries.LS_listCsActivities, "LS_listCsActivities", {
      storeId,
      orderId,
      taskId,
      start,
      end,
      page,
      size,
    });
  }

  searchCsOrders(storeId, keyword, limit = 20) {
    return this._callDeliveryField(queries.LS_searchCsOrders, "LS_searchCsOrders", {
      storeId,
      keyword,
      limit,
    });
  }

  // ----- 클레임 (취소/반품/교환/철회) -----

  cancelDeliveryOrder(
    storeId,
    orderId,
    { items, reasonCode, refundMethodCode, refundBank, paymentGatewayCancel, employeeId } = {},
  ) {
    return this._callDeliveryField(mutations.LS_cancelDeliveryOrder, "LS_cancelDeliveryOrder", {
      storeId,
      orderId,
      items: items ? JSON.stringify(items) : null,
      reasonCode,
      refundMethodCode,
      refundBank: refundBank ? JSON.stringify(refundBank) : null,
      paymentGatewayCancel,
      employeeId,
    });
  }

  restoreDeliveryOrder(storeId, orderId, undoneReasonType, employeeId) {
    return this._callDeliveryField(mutations.LS_restoreDeliveryOrder, "LS_restoreDeliveryOrder", {
      storeId,
      orderId,
      undoneReasonType,
      employeeId,
    });
  }

  // claimCode 생략 시 서버가 최신 미정산 반품을 자동 선택.
  restoreDeliveryReturn(storeId, orderId, undoneReasonType, employeeId) {
    return this._callDeliveryField(mutations.LS_restoreDeliveryReturn, "LS_restoreDeliveryReturn", {
      storeId,
      orderId,
      claimCode: undefined,
      undoneReasonType,
      employeeId,
    });
  }

  // groupId 생략 시 서버가 최신 미정산 교환을 자동 선택.
  restoreDeliveryExchange(storeId, orderId, undoneReasonType, employeeId) {
    return this._callDeliveryField(
      mutations.LS_restoreDeliveryExchange,
      "LS_restoreDeliveryExchange",
      {
        storeId,
        orderId,
        groupId: undefined,
        undoneReasonType,
        employeeId,
      },
    );
  }

  // AWSJSON 인자 stringify — 값 없으면 null
  _json(v) {
    return v !== undefined && v !== null ? JSON.stringify(v) : null;
  }

  // 확장/모달 인자 번들 — undefined/null 제거 후 AWSJSON 문자열(빈 객체면 null)
  _optionsJson(options) {
    const clean = {};
    Object.keys(options || {}).forEach((k) => {
      if (options[k] !== undefined && options[k] !== null) clean[k] = options[k];
    });
    return Object.keys(clean).length ? JSON.stringify(clean) : null;
  }

  // 확장 인자는 배포 스키마 기준 options(AWSJSON) 번들로 전송 — base(items) 외 전부 options.
  acceptDeliveryReturn(storeId, orderId, payload = {}) {
    const { items, employeeId, ...options } = payload;
    return this._callDeliveryField(mutations.LS_acceptDeliveryReturn, "LS_acceptDeliveryReturn", {
      storeId,
      orderId,
      items: this._json(items),
      options: this._optionsJson(options),
      employeeId,
    });
  }

  receiveDeliveryReturn(storeId, orderId, payload = {}) {
    const { pickupCompleted, employeeId, ...options } = payload;
    return this._callDeliveryField(
      mutations.LS_receiveDeliveryReturn,
      "LS_receiveDeliveryReturn",
      { storeId, orderId, pickupCompleted, options: this._optionsJson(options), employeeId },
    );
  }

  completeDeliveryReturn(storeId, orderId, refundMethodCode, employeeId) {
    return this._callDeliveryField(
      mutations.LS_completeDeliveryReturn,
      "LS_completeDeliveryReturn",
      { storeId, orderId, refundMethodCode, employeeId },
    );
  }

  acceptDeliveryExchange(storeId, orderId, payload = {}) {
    const { returnItems, exchangeItems, employeeId, ...options } = payload;
    return this._callDeliveryField(
      mutations.LS_acceptDeliveryExchange,
      "LS_acceptDeliveryExchange",
      {
        storeId,
        orderId,
        returnItems: this._json(returnItems),
        exchangeItems: this._json(exchangeItems),
        options: this._optionsJson(options),
        employeeId,
      },
    );
  }

  receiveDeliveryExchange(storeId, orderId, payload = {}) {
    const { employeeId, ...options } = payload;
    return this._callDeliveryField(
      mutations.LS_receiveDeliveryExchange,
      "LS_receiveDeliveryExchange",
      { storeId, orderId, options: this._optionsJson(options), employeeId },
    );
  }

  createRedelivery(storeId, orderId, payload = {}) {
    // 재배송 — 회수 없이 새 출고건 적재(발번은 이후 송장 플로우). items=[{skuId, quantity, price?}]
    // faultBy 미지정 = 서버 기본 seller / receiver 미지정 = 원 배송지 복제
    const { items, reasonCode, faultBy, receiver, locationId, employeeId } = payload;
    return this._callDeliveryField(
      mutations.LS_createDeliveryRedelivery,
      "LS_createDeliveryRedelivery",
      {
        storeId,
        orderId,
        items: this._json(items),
        reasonCode,
        faultBy,
        receiver: this._json(receiver),
        locationId,
        employeeId,
      },
    );
  }

  // groupId — 완료할 교환 그룹(exchangeGroupId). 미지정이면 서버가 첫 교환 엔트리를 잡는다.
  completeDeliveryExchange(storeId, orderId, employeeId, groupId) {
    return this._callDeliveryField(
      mutations.LS_completeDeliveryExchange,
      "LS_completeDeliveryExchange",
      { storeId, orderId, groupId: groupId || undefined, employeeId },
    );
  }

  // ----- 주문 편집·운영 -----

  setDeliveryHold(storeId, orderId, enabled, reasonCode, employeeId) {
    return this._callDeliveryField(mutations.LS_setDeliveryHold, "LS_setDeliveryHold", {
      storeId,
      orderId,
      enabled,
      reasonCode,
      employeeId,
    });
  }

  updateDeliveryReceiver(storeId, orderId, receiver, employeeId) {
    // receiver: { name, phone, zip, address1, address2, note }
    const { name, phone, zip, address1, address2, note } = receiver || {};
    return this._callDeliveryField(
      mutations.LS_updateDeliveryReceiver,
      "LS_updateDeliveryReceiver",
      { storeId, orderId, name, phone, zip, address1, address2, note, employeeId },
    );
  }

  addDeliveryOrderItems(storeId, orderId, items, paymentOption, employeeId) {
    return this._callDeliveryField(
      mutations.LS_addDeliveryOrderItems,
      "LS_addDeliveryOrderItems",
      {
        storeId,
        orderId,
        items: items ? JSON.stringify(items) : null,
        paymentOption,
        employeeId,
      },
    );
  }

  swapDeliveryOrderItem(storeId, orderId, orderItemId, newSkuId, paymentOption, employeeId) {
    return this._callDeliveryField(
      mutations.LS_swapDeliveryOrderItem,
      "LS_swapDeliveryOrderItem",
      { storeId, orderId, orderItemId, newSkuId, paymentOption, employeeId },
    );
  }

  handleDeliveryShortage(storeId, skuId, action, orderIds, employeeId) {
    // action: "hold" | "cancel" | "split"
    return this._callDeliveryField(
      mutations.LS_handleDeliveryShortage,
      "LS_handleDeliveryShortage",
      { storeId, skuId, action, orderIds, employeeId },
    );
  }

  openInquiry(storeId, orderId, reasonCode, memo, employeeId) {
    return this._callDeliveryField(mutations.LS_openInquiry, "LS_openInquiry", {
      storeId,
      orderId,
      reasonCode,
      memo,
      employeeId,
    });
  }

  completeInquiry(storeId, orderId, taskId, employeeId) {
    // 문의(GENERAL) 응대 완료 — taskId 단건 / 미지정 시 주문의 활성 전건 DONE
    return this._callDeliveryField(mutations.LS_completeInquiry, "LS_completeInquiry", {
      storeId,
      orderId,
      taskId,
      employeeId,
    });
  }

  // ----- 결제·정산 -----

  confirmDeliveryPayment(storeId, orderId, amount, method, extraMethod, employeeId) {
    return this._callDeliveryField(
      mutations.LS_confirmDeliveryPayment,
      "LS_confirmDeliveryPayment",
      { storeId, orderId, amount, method, extraMethod, employeeId },
    );
  }

  settleDeliveryReturn(storeId, orderId, claimCode, employeeId) {
    return this._callDeliveryField(
      mutations.LS_settleDeliveryReturn,
      "LS_settleDeliveryReturn",
      { storeId, orderId, claimCode, employeeId },
    );
  }

  // ----- 수집·승인 -----

  collectClaimRequests(storeId, { days, start, end } = {}) {
    return this._callDeliveryField(
      mutations.LS_collectClaimRequests,
      "LS_collectClaimRequests",
      { storeId, days, start, end },
    );
  }

  collectDeliveryInquiries(storeId, { boardNos, days } = {}) {
    return this._callDeliveryField(
      mutations.LS_collectDeliveryInquiries,
      "LS_collectDeliveryInquiries",
      { storeId, boardNos, days },
    );
  }

  // 2026-07-20 수집함 조회 전용 전환 — UI 미사용. 채널 수락 통보 재구조(승인=접수만) 확정 후 재사용/제거 결정
  // 상세 인자 = options(AWSJSON) 번들 — LS_acceptDeliveryReturn/Exchange 와 동일 계약.
  // flat 나열은 AppSync 가 미지정분까지 null 로 채워 보내 서버 라우팅에서 TypeError 를 냈고,
  // 인자명도 options 경로와 어긋났다(locationId vs returnShippingLocationId).
  // items/returnItems/exchangeItems 는 처리함수의 flat 파라미터라 번들에서 분리해 보낸다.
  approveClaimRequest(storeId, claimType, { orderId, employeeId, ...rest } = {}) {
    const { items, returnItems, exchangeItems, ...options } = rest;
    const vars = this._stripUndefined({
      storeId, orderId, claimType, employeeId,
      items, returnItems, exchangeItems,
      options: Object.keys(options).length > 0 ? options : undefined,
    });
    ["items", "returnItems", "exchangeItems", "options"].forEach((key) => {
      if (vars[key] !== undefined && typeof vars[key] !== "string") {
        vars[key] = JSON.stringify(vars[key]);
      }
    });
    return this._callDeliveryField(
      mutations.LS_approveClaimRequest,
      "LS_approveClaimRequest",
      vars,
    );
  }

  // taskId — 거절할 CSTask 지정(앱 클레임). 미지정이면 서버가 (orderId, claimType) 활성 task 를 잡는다.
  rejectClaimRequest(storeId, orderId, claimType, orderItemIds, rejectReason, employeeId, taskId) {
    return this._callDeliveryField(
      mutations.LS_rejectClaimRequest,
      "LS_rejectClaimRequest",
      {
        storeId,
        orderId,
        claimType,
        taskId: taskId || undefined,
        orderItemIds,
        rejectReason,
        employeeId,
      },
    );
  }

  // ----- 취소마감/환불이체/재동기화/회수발번 — 2026-07-10 스키마 배포 확인(시그니처 대조 완료) -----
  // todo: 응답 shape 는 실데이터로 최종 확인

  completeDeliveryCancel(storeId, orderId, claimCode, employeeId) {
    // 취소 수동 완료처리(무통장 입금완료 후 마감)
    return this._callDeliveryField(
      mutations.LS_completeDeliveryCancel,
      "LS_completeDeliveryCancel",
      { storeId, orderId, claimCode, employeeId },
    );
  }

  completeRefundTransfer(
    storeId,
    orderId,
    { taskId, refundCode, sendSms, sendMail, employeeId } = {},
  ) {
    // 계좌 환불(이체) 실행 완료 처리
    return this._callDeliveryField(
      mutations.LS_completeRefundTransfer,
      "LS_completeRefundTransfer",
      { storeId, orderId, taskId, refundCode, sendSms, sendMail, employeeId },
    );
  }

  resyncChannel(storeId, orderId, taskId, employeeId) {
    // 채널미반영 재동기화
    return this._callDeliveryField(mutations.LS_resyncChannel, "LS_resyncChannel", {
      storeId,
      orderId,
      taskId,
      employeeId,
    });
  }

  issuePickupTracking(
    storeId,
    orderId,
    { claimCode, locationId, boxQty, trackingNumber, carrierCode, employeeId } = {},
  ) {
    // 회수(반송) 송장 발번
    return this._callDeliveryField(
      mutations.LS_issuePickupTracking,
      "LS_issuePickupTracking",
      { storeId, orderId, claimCode, locationId, boxQty, trackingNumber, carrierCode, employeeId },
    );
  }

}

const instance = new API();
// 데모 리포트 모드 (시연 계정 한정) — 켜져 있으면 리포트/이벤트/캠페인 조회를 정적 번들로 대체한다.
// 꺼져 있거나 다른 매장이면 아무 것도 바꾸지 않는다. src/demo/interceptor.js 참조.
installDemoInterceptor(instance);
export default instance;
